/**
 * The colony simulation — everything beneath the soil that *decides* something.
 *
 * Ants, brood, the queen, the tunnel network, the systems that move them, and
 * one `update(dt)` that advances the nest. The underground half of the
 * deterministic district core (`IMPLEMENTATION-GOVERNORS.md` §5), and the twin
 * of `SurfaceSim` one layer up. It obeys the five rules in
 * `docs/determinism.md`:
 *
 *   - every stochastic decision draws from `rng`, never from `Math.random`
 *   - time comes from `gameTimeMs`, never from the wall clock
 *   - no DOM, no storage, no canvas; rendering, the camera and input live in
 *     `FormicariumLayer`, which owns this object and draws it
 *   - iteration that affects state is over arrays or insertion-ordered maps
 *   - `snapshot()` is total and `canonicalState()` includes the generator
 *
 * The practical test of all five is that this class constructs and runs in Node
 * with `Math.random` poisoned and both clocks frozen, which is what
 * `tests/determinism.test.ts` does.
 */

import { Portal, TransferableEntity, PortalBuilder, AntTransferData } from '@antification/protocol';
import { anomaly } from '../diagnostics';
import { EventBus, GameEventTypes } from '../EventBus';
import { Ant, AntRole, AntRoleAttributes, AntSnapshot, AntState, AntTask, AntNameGenerator, Position, AntExportData, TaskType, ScentType, DEFAULT_ANT_ROLE_ATTRIBUTES } from './Ant';
import { TunnelNetwork, Chamber, ChamberType, STIMULUS_KEYS, TunnelNetworkSnapshot } from './TunnelNetwork';
import { RoleTransitionSystem, RoleTransitionContext, RoleTransitionConfig } from './systems/RoleTransitionSystem';
import { NavigationSystem, NavigationContext } from './systems/NavigationSystem';
import { DamageSystem, DamageContext, DebrisParticle, DamageConfig, PendingDamage } from './systems/DamageSystem';
import { MetabolismSystem, MetabolismContext, MetabolismConfig } from './systems/MetabolismSystem';
import { BroodSystemConfig } from './systems/BroodSystem';
import { StimulusConfig } from './systems/StimulusSystem';
import { Rng, RngState, Sfc32Rng } from '../Rng';
import { Canonical, hashState } from '../hashState';
import { SNAPSHOT_VERSION, requireSnapshotVersion } from '../snapshot';
import { advanceTimer, refTicks, rollPerRefTick } from '../SimClock';

export interface ColonyStats {
  initialPopulation: number;
  currentPopulation: number;
  peakPopulation: number;
  deaths: number;
  births: number;
  foodGathered: number;
  queenStatus: 'alive' | 'deceased';
  colonyAge: number; // in ticks
}

export interface ColonyNewsItem {
  message: string;
  timestamp: number;
  type: 'info' | 'warning' | 'alert' | 'death';
}

// Brood stages (biologically accurate ant lifecycle)
export enum BroodStage {
  EGG = 'egg',       // Laid by queen, carried to nursery
  LARVA = 'larva',   // Needs feeding, grows
  PUPA = 'pupa',     // Metamorphosis, no feeding needed
}

/**
 * An egg, a larva or a pupa.
 *
 * All data and no behaviour, so a snapshot of one is the record itself — unlike
 * an ant, which is an object with a generator, a role and a career.
 */
export interface Brood {
  id: number;
  stage: BroodStage;
  position: Position;
  chamberId: number;      // Which chamber it's in
  age: number;            // Time in current stage (ms)
  fed: number;            // How much food this larva has received (0-100)
  carriedByAntId: number | null; // If being carried, which ant
  droppedTime: number;    // When egg was last dropped (gameTimeMs) - needs to settle before pickup
}

/**
 * One ant colony, entire.
 *
 * Constructed with the world seed and its own index: the index picks the tunnel
 * layout and forks the generator, so three colonies of one world live three
 * different lives from the same seed.
 */
export class ColonySim {
  /** The id this colony stamps its events and portals with. The client's layer shares it. */
  readonly id: string;
  readonly colonyIndex: number;

  /** Portals this colony exposes to the surface. Wiring, not season state. */
  readonly portals: Portal[] = [];

  private readonly events: EventBus;

  // Core simulation
  /**
   * Every stochastic decision this colony makes flows from here. Seeded per
   * (world, colony) so the same colony replays identically whether or not
   * anybody is watching it. See `docs/determinism.md`.
   */
  private rng: Rng;
  readonly ants: Map<number, Ant> = new Map();
  readonly tunnelNetwork: TunnelNetwork;
  private nextAntId = 0;

  // Special ants
  private queen: Ant | null = null;
  private philosopherAnt: Ant | null = null;
  private kira: Ant | null = null; // The protagonist forager
  private soldierVex: Ant | null = null;
  private oldMap: Ant | null = null;

  // Colony state
  private stats: ColonyStats;
  private news: ColonyNewsItem[] = [];
  private alertState: boolean = false;
  private alertTimer = 0;

  // Simulation timing
  private tickCount = 0;
  private gameTimeMs = 0;  // Total elapsed time in milliseconds (for cooldowns)
  private pheromoneDecayTimer = 0;

  // Queen state
  private queenHunger = 0;

  // Colony config (mutable for live settings)
  private colonyConfig = {
    PHEROMONE_DECAY_INTERVAL: 500,
    PHEROMONE_DECAY_RATE: 0.02,
    QUEEN_HUNGER_RATE: 0.1,
    QUEEN_HUNGER_MAX: 100,
    QUEEN_FEED_AMOUNT: 20,
    ANT_HUNGER_MAX: 100,
  };

  // Colony state
  private colonyState: 'normal' | 'queenless' | 'replacing_queen' = 'normal';
  private replacementTimer = 0;
  private replacementCandidate: Ant | null = null;

  // Named ants tracking
  private namedByPlayer: Set<number> = new Set();

  // Ants currently on surface (via portal)
  readonly antsOnSurface: Set<number> = new Set();

  // Brood (eggs, larvae, pupae)
  readonly brood: Map<number, Brood> = new Map();
  private nextBroodId = 0;

  // Per-colony ant role attributes (set from Game.ts via setRoleAttributes)
  private roleAttributes: Record<AntRole, AntRoleAttributes> = { ...DEFAULT_ANT_ROLE_ATTRIBUTES };

  // Brood config (mutable for live settings) - colony-level copies for inline usage
  private broodConfig = {
    EGG_DURATION: 10000,
    LARVA_DURATION: 20000,
    PUPA_DURATION: 15000,
    LARVA_FOOD_NEEDED: 25,
    EGG_LAY_INTERVAL: 2500,
    MAX_BROOD: 50,
  };

  // Queen egg laying
  private eggLayTimer = 0;

  // Stimulus config (mutable for live settings) - colony-level copies for inline usage
  private stimConfig = {
    STIMULI_UPDATE_INTERVAL: 500,
    STIMULUS_DECAY_RATE: 0.05,
    SWITCHING_COST: 0.08,
    BASELINE_ACTIVITY: 0.15,
  };

  // Stimulus timing
  private stimuliTimer = 0;

  // Surface portal position (grid coordinates) - set by the district's wiring
  private surfacePortalX: number = -1;
  private surfacePortalY: number = -1;

  // Role transition system for dynamic role changes
  private roleTransitionSystem: RoleTransitionSystem;

  // Navigation system for scent-based ant movement
  private navigationSystem: NavigationSystem;

  // Damage system for construction-related damage
  private damageSystem: DamageSystem;

  // Metabolism system for colony food consumption
  private metabolismSystem: MetabolismSystem;

  constructor(events: EventBus, colonyIndex: number = 0, worldSeed: string = 'parkington') {
    this.events = events;

    this.colonyIndex = colonyIndex;
    this.id = `formicarium-${colonyIndex}`;

    // One stream per colony, derived from the world seed. Colonies consume
    // randomness at wildly different rates, so they must not share a stream.
    this.rng = new Sfc32Rng(`${worldSeed}/colony-${colonyIndex}`);

    // Each colony gets a different tunnel network layout based on its index
    this.tunnelNetwork = new TunnelNetwork(800, 400, 5, colonyIndex, this.rng);

    // Initialize role transition system with faster checks
    this.roleTransitionSystem = new RoleTransitionSystem(this.rng, {
      TRANSITION_CHECK_INTERVAL: 10000, // Check every 10 seconds
      MIN_COLONY_SIZE: 10, // Allow transitions in smaller colonies
    });

    // Initialize navigation system
    this.navigationSystem = new NavigationSystem();

    // Initialize damage system
    this.damageSystem = new DamageSystem(this.rng);

    // Initialize metabolism system
    this.metabolismSystem = new MetabolismSystem();

    this.stats = {
      initialPopulation: 847, // The magic number
      currentPopulation: 0,
      peakPopulation: 0,
      deaths: 0,
      births: 0,
      foodGathered: 0,
      queenStatus: 'alive',
      colonyAge: 0,
    };

    // Listen for construction events
    this.events.on(GameEventTypes.GRADING_VIBRATION, this.handleVibration.bind(this));
    this.events.on(GameEventTypes.CONCRETE_POURED, this.handleConcretePour.bind(this));
    this.events.on(GameEventTypes.EXCAVATION_STARTED, this.handleExcavation.bind(this));

    // Listen for surface death events - only handle deaths for this colony
    this.events.on(GameEventTypes.ANT_KILLED_ON_SURFACE, (payload) => {
      const data = payload as { antId: number; name: string; colonyIndex?: number };
      // Only handle if this death is for our colony
      if (data.colonyIndex === undefined || data.colonyIndex === this.colonyIndex) {
        this.handleSurfaceDeath(payload);
      }
    });


    // Register event types for this layer
    this.events.registerEventTypes(this.id, [
      GameEventTypes.ANT_BORN,
      GameEventTypes.ANT_DIED,
      GameEventTypes.ANT_NAMED,
      GameEventTypes.ANT_EXITED,
      GameEventTypes.ANT_ENTERED,
      GameEventTypes.ANT_KILLED_ON_SURFACE,
      GameEventTypes.TUNNEL_COLLAPSED,
      GameEventTypes.CHAMBER_DESTROYED,
      GameEventTypes.COLONY_ALERT,
      GameEventTypes.QUEEN_DIED,
      GameEventTypes.COLONY_EXTINCT,
    ]);
  }

  /**
   * Dig the nest and fill it.
   *
   * Separate from the constructor because it emits portals a caller has to wire
   * into a `LayerManager`, and because it is where the colony's first two
   * headlines are filed.
   */
  init(): void {
    // Generate the tunnel network
    this.tunnelNetwork.generateNetwork();

    // Populate the colony
    this.populateColony();

    // Set up portals (exit points that lead to surface)
    this.setupPortals();

    this.addNews('Colony simulation initialized.', 'info');
    this.addNews(`Queen ${this.queen?.name} presides over ${this.stats.currentPopulation} subjects.`, 'info');
  }

  /**
   * Set up bidirectional portals at entrance chambers.
   * Ants can exit to surface and return through the same portal.
   * Portal IDs are unique per colony.
   */
  private setupPortals(): void {
    const entrances = this.tunnelNetwork.getEntrances();

    entrances.forEach((entrance, index) => {
      const portal = PortalBuilder.create(`colony-${this.colonyIndex}-exit-${index + 1}`)
        .inLayer(this.id)
        .connectsTo('parkington', `surface-entrance-colony-${this.colonyIndex}-${index + 1}`)
        .at(entrance.position.x, entrance.position.y)
        .ofType('bidirectional')
        .allowsEntities('ant')
        .withRadius(entrance.radius)
        .build();

      this.portals.push(portal);
    });
  }

  /**
   * Export an ant's data for transfer to another layer.
   * Uses the Ant's built-in export() method.
   */
  exportAnt(ant: Ant): AntExportData {
    return ant.export();
  }

  /**
   * Send an ant to the surface via a portal.
   */
  sendAntToSurface(antId: number, portalId: string): TransferableEntity | null {
    const ant = this.ants.get(antId);
    if (!ant || !ant.isAlive()) return null;

    const portal = this.portals.find(p => p.id === portalId);
    if (!portal) return null;

    // NOTE: Do NOT add to antsOnSurface here. The transfer may fail.
    // Game.ts calls confirmAntOnSurface() after successful transfer.

    // Create transferable entity
    const entity: TransferableEntity = {
      type: 'ant',
      data: this.exportAnt(ant),
      sourcePortal: portalId,
      sourceLayer: this.id,
      timestamp: this.gameTimeMs,
    };

    return entity;
  }

  /**
   * Confirm that an ant successfully transferred to the surface.
   * Called by Game.ts after LayerManager.transferEntity() succeeds.
   */
  confirmAntOnSurface(antId: number): void {
    this.antsOnSurface.add(antId);

    const ant = this.ants.get(antId);
    if (ant) {
      this.events.emit(GameEventTypes.ANT_EXITED, {
        antId,
        name: ant.getDisplayName(),
        portalId: '',
        colonyIndex: this.colonyIndex,
      }, this.id);
    }
  }

  /**
   * Receive an entity returning from another layer.
   *
   * Returns whether the colony took it, per the transfer contract (ADR-028). The
   * colony's half of that contract has no refusal in it and is not expected to
   * grow one: the surface can be sealed over, and a burrow that has lost its way
   * out has lost it at the top. An ant that never gets home is stopped by the
   * ground above, not turned away down here.
   */
  receiveEntity(entity: TransferableEntity, fromPortal: string): boolean {
    if (entity.type === 'ant') {
      this.handleAntReturn(entity.data as AntExportData, fromPortal);
      return true;
    }
    return false;
  }

  /**
   * Handle an ant returning from the surface.
   */
  private handleAntReturn(antData: AntExportData, portalId: string): void {
    const ant = this.ants.get(antData.id);
    if (!ant) {
      anomaly(`[ANT_RETURN] Ant ${antData.id} not found in colony ${this.colonyIndex} ants map`);
      // Still clean up tracking in case it was in the set
      this.antsOnSurface.delete(antData.id);
      return;
    }

    // Mark ant as back in colony
    this.antsOnSurface.delete(antData.id);

    // Update ant from imported data
    ant.import(antData);

    // Update ant position to portal location
    const portal = this.portals.find(p => p.id === portalId);
    if (portal) {
      ant.position.x = portal.position.x;
      ant.position.y = portal.position.y;
    }

    // Reset task for colony-side behavior
    // If carrying food, set task to CARRYING_FOOD (local sensing will handle deposit)
    if (ant.carryingFood && ant.carriedFoodAmount > 0) {
      ant.task = AntTask.CARRYING_FOOD;
      this.setScent(ant, ScentType.FOOD_SMELL);
    } else {
      ant.task = AntTask.NONE;
    }

    // Initialize scent-based navigation state
    const chamber = this.tunnelNetwork.getChamberAt(ant.position);
    if (chamber) {
      ant.currentChamberId = chamber.id;
    }
    ant.currentTunnelId = null;
    ant.recentTunnels = [];

    // Emit event
    this.events.emit(GameEventTypes.ANT_ENTERED, {
      antId: antData.id,
      name: ant.getDisplayName(),
      portalId,
      colonyIndex: this.colonyIndex,
    }, this.id);
  }

  private populateColony(): void {
    const queenChamber = this.tunnelNetwork.getQueenChamber();

    // Create the queen
    if (queenChamber) {
      this.queen = this.createAnt(AntRole.QUEEN, queenChamber.position);
      this.queen.name = AntNameGenerator.generateQueenName(7); // VII
      this.queen.stats.eggsNursed = 50000; // She's been busy
    }

    // Create the special named characters
    this.createSpecialAnts();

    // Create the rest of the population
    const targetPopulation = 140;
    const distribution = {
      [AntRole.FORAGER]: 0.35,  // Reduced from 45% - foragers go outside often
      [AntRole.SOLDIER]: 0.15,
      [AntRole.NURSE]: 0.40,    // Increased - larvae need constant attention
      [AntRole.ENGINEER]: 0.05, // Reduced - only need a few for repairs
    };

    const chambers = this.tunnelNetwork.getAllChambers().filter(c => !c.destroyed);

    // ORDERING: the roulette below must walk the roles in a fixed order.
    // It used to iterate Object.entries(distribution); those are string keys,
    // so V8 returns them in insertion order -- correct today, and silently
    // reshuffling the colony's whole role mix the day someone reorders the
    // object literal. Made explicit instead. Must match the literal above:
    // the cumulative bands depend on the order, not just the weights.
    const ROLE_ROLL_ORDER: readonly (keyof typeof distribution)[] = [
      AntRole.FORAGER,
      AntRole.SOLDIER,
      AntRole.NURSE,
      AntRole.ENGINEER,
    ];

    while (this.ants.size < targetPopulation) {
      // Pick a role based on distribution
      const rand = this.rng.next();
      let role: AntRole;
      let cumulative = 0;

      for (const r of ROLE_ROLL_ORDER) {
        cumulative += distribution[r];
        if (rand <= cumulative) {
          role = r;
          break;
        }
      }
      role = role! || AntRole.FORAGER;

      // Pick a random chamber
      const chamber = this.rng.pick(chambers)!;
      const pos = this.randomPositionInChamber(chamber);

      const ant = this.createAnt(role, pos);

      // Start with fresh stats (0) but randomize biological state
      // Random hunger level (0-50) - some ants start hungry
      ant.hunger = this.rng.int(50);
      // Random energy level (50-100) - all start reasonably energetic
      ant.fatigue = this.rng.int(50);

      // Random age distribution (young colony, most ants are young)
      ant.age = Math.floor(this.rng.next() * ant.maxAge * 0.3);
    }

    this.stats.currentPopulation = this.ants.size;
    this.stats.peakPopulation = this.ants.size;
  }

  private createSpecialAnts(): void {
    const nursery = this.tunnelNetwork.getNursery();
    const entrances = this.tunnelNetwork.getEntrances();
    const foodStorages = this.tunnelNetwork.getFoodStorages();

    // Kira-1184: The protagonist forager
    if (entrances.length > 0) {
      this.kira = this.createAnt(AntRole.FORAGER, this.randomPositionInChamber(entrances[0]));
      this.kira.name = 'Kira-1184';
      this.kira.stats.foragingTrips = 847; // The magic number
      this.kira.stats.foodGathered = 847 * 15;
      this.kira.stats.distanceTraveled = 847 * 100;
      this.kira.age = 80000; // Experienced but not elderly
      // Her mother died in "the rain event"
      this.kira.addMemory({
        type: 'death',
        position: { x: 100, y: 50 },
        timestamp: -50000,
        detail: 'Mother lost in the rain event',
      });
    }

    // Soldier Vex: The scarred veteran
    if (entrances.length > 0) {
      this.soldierVex = this.createAnt(AntRole.SOLDIER, this.randomPositionInChamber(entrances[0]));
      this.soldierVex.name = 'Vex';
      this.soldierVex.size = 1.5; // Largest soldier
      this.soldierVex.scarred = true;
      this.soldierVex.stats.threatsRepelled = 3; // Spider incursions
      this.soldierVex.addMemory({
        type: 'danger',
        position: { x: 200, y: 100 },
        timestamp: -30000,
        detail: 'The centipede',
      });
    }

    // Old Map: The elderly forager
    if (foodStorages.length > 0) {
      this.oldMap = this.createAnt(AntRole.FORAGER, this.randomPositionInChamber(foodStorages[0]));
      this.oldMap.name = 'Old Map';
      this.oldMap.age = this.oldMap.maxAge * 0.95; // Very old
      this.oldMap.speed = this.oldMap.baseSpeed * 0.5; // Moves slowly
      this.oldMap.stats.foragingTrips = 2000;
      this.oldMap.stats.distanceTraveled = 200000;
    }

    // The Philosopher: One ant who sometimes just... thinks
    const randomChamber = this.rng.pick(this.tunnelNetwork.getAllChambers())!;
    this.philosopherAnt = this.createAnt(AntRole.FORAGER, this.randomPositionInChamber(randomChamber));
    this.philosopherAnt.isPhilosopher = true;
    this.philosopherAnt.name = AntNameGenerator.generateSpecialName(this.rng);

    // Yira-7732: Longest-serving nurse
    if (nursery) {
      const yira = this.createAnt(AntRole.NURSE, this.randomPositionInChamber(nursery));
      yira.name = 'Yira-7732';
      yira.age = 100000;
      yira.stats.eggsNursed = 5000;
    }

    // Glorious Mandate IX: Perfectly average
    const avgChamber = this.tunnelNetwork.getAllChambers()[5];
    if (avgChamber) {
      const avg = this.createAnt(AntRole.FORAGER, this.randomPositionInChamber(avgChamber));
      avg.name = 'Glorious Mandate IX';
      // Perfectly median stats will be calculated later
    }
  }

  private createAnt(role: AntRole, position: Position): Ant {
    const id = this.nextAntId++;
    const name = role === AntRole.QUEEN
      ? AntNameGenerator.generateQueenName(7)
      : AntNameGenerator.generate(role, id, this.rng);

    const ant = new Ant(id, name, role, position, this.tickCount, this.rng, this.roleAttributes);
    this.ants.set(id, ant);

    return ant;
  }

  private randomPositionInChamber(chamber: Chamber): Position {
    const angle = this.rng.next() * Math.PI * 2;
    const r = this.rng.next() * chamber.radius * 0.7;
    return {
      x: chamber.position.x + Math.cos(angle) * r,
      y: chamber.position.y + Math.sin(angle) * r,
    };
  }

  // --- Read access, for a client that draws this colony ---------------------

  /** Ticks advanced. */
  get tick(): number {
    return this.tickCount;
  }

  /** Sim-milliseconds elapsed. The colony's only clock. */
  get elapsedMs(): number {
    return this.gameTimeMs;
  }

  /** The queen, or null if the colony has none. */
  getQueen(): Ant | null {
    return this.queen;
  }

  /** How much a larva must be fed to pupate. Read by the renderer to size it. */
  get larvaFoodNeeded(): number {
    return this.broodConfig.LARVA_FOOD_NEEDED;
  }

  /** Whether an ant has arrived at its target. Used for the walk animation. */
  isAtTarget(ant: Ant): boolean {
    return this.navigationSystem.isAtTarget(ant);
  }

  /**
   * Give an ant the name a player typed.
   *
   * The one thing a mayor can do to a colony, and it reaches the world directly
   * rather than through a verb — the same gap `docs/determinism.md` records for
   * save and load. Here so that the client never writes colony state itself.
   */
  nameAnt(antId: number, name: string): boolean {
    const ant = this.ants.get(antId);
    if (!ant) return false;
    const trimmed = name.trim();
    if (!trimmed) return false;

    ant.customName = trimmed;
    this.namedByPlayer.add(ant.id);
    this.addNews(`${ant.name} has been named "${trimmed}"`, 'info');
    return true;
  }

  // ======== Damage Methods (delegated to DamageSystem) ========

  private getDamageContext(): DamageContext {
    return {
      tunnelNetwork: this.tunnelNetwork,
      ants: this.ants,
      surfacePortalX: this.surfacePortalX,
      surfacePortalY: this.surfacePortalY,
      gameTimeMs: this.gameTimeMs,
      layerId: this.id,
      addNews: (msg, type) => this.addNews(msg, type as 'info' | 'warning' | 'alert' | 'death'),
      killAnt: (ant, cause) => this.killAnt(ant, cause),
      setAlertState: (alert, timer) => {
        if (!this.alertState) {
          this.alertState = alert;
          this.alertTimer = timer;
        }
      },
      events: this.events,
    };
  }

  private handleVibration(payload: unknown): void {
    this.damageSystem.handleVibration(payload, this.getDamageContext());
  }

  private handleExcavation(payload: unknown): void {
    this.damageSystem.handleExcavation(payload, this.getDamageContext());
  }

  private handleConcretePour(payload: unknown): void {
    this.damageSystem.handleConcretePour(payload, this.getDamageContext());
  }

  private updateDebrisParticles(dt: number): void {
    this.damageSystem.update(dt, this.getDamageContext());
  }

  getDebrisParticles(): DebrisParticle[] {
    return this.damageSystem.getDebrisParticles();
  }

  /**
   * Handle an ant being killed on the surface (e.g., by a car).
   */
  private handleSurfaceDeath(payload: unknown): void {
    const data = payload as { antId: number; name: string; cause: string };

    const ant = this.ants.get(data.antId);
    if (!ant) {
      anomaly(`[SURFACE_DEATH] Ant ${data.antId} not found in colony ${this.colonyIndex} ants map`);
      // Still clean up tracking
      this.antsOnSurface.delete(data.antId);

      return;
    }

    // Remove from surface tracking
    this.antsOnSurface.delete(data.antId);

    // Kill the ant
    ant.state = AntState.DEAD;
    ant.health = 0;
    this.stats.deaths++;
    this.stats.currentPopulation--;

    // Add news about the death
    const displayName = ant.customName || ant.name;
    this.addNews(`${displayName} was lost on the surface.`, 'death');

    // Special death notifications for named ants
    if (ant === this.kira) {
      this.addNews('Kira-1184 will forage no more.', 'death');
    } else if (ant === this.oldMap) {
      this.addNews('Old Map\'s final journey ended above ground.', 'death');
    } else if (ant === this.soldierVex) {
      this.addNews('Vex fell to a danger even greater than the centipede.', 'death');
    }

    // Emit the standard death event
    this.events.emit(GameEventTypes.ANT_DIED, {
      id: ant.id,
      name: displayName,
      role: ant.role,
      cause: data.cause,
    }, this.id);

    // Check for extinction
    if (this.stats.currentPopulation <= 0) {
      this.events.emit(GameEventTypes.COLONY_EXTINCT, {
        finalPopulation: 0,
        lastSurvivor: displayName,
      }, this.id);
    }
  }

  private killAnt(ant: Ant, cause: string): void {
    ant.state = AntState.DEAD;
    ant.health = 0;
    this.antsOnSurface.delete(ant.id); // Clean up in case ant was tracked as on-surface
    this.stats.deaths++;
    this.stats.currentPopulation--;

    // Special death notifications
    if (ant === this.queen) {
      this.stats.queenStatus = 'deceased';
      this.addNews(`Queen ${ant.name} has fallen. The colony mourns.`, 'death');
      this.events.emit(GameEventTypes.QUEEN_DIED, { name: ant.name, cause }, this.id);
    } else if (ant === this.kira) {
      this.addNews(`${ant.getDisplayName()} has stopped moving.`, 'death');
    } else if (ant === this.oldMap) {
      this.addNews('Old Map has stopped moving.', 'death');
    } else if (this.namedByPlayer.has(ant.id)) {
      this.addNews(`${ant.getDisplayName()} has stopped moving.`, 'death');
    }

    this.events.emit(GameEventTypes.ANT_DIED, {
      id: ant.id,
      name: ant.getDisplayName(),
      role: ant.role,
      cause,
    }, this.id);

    // Check for extinction
    if (this.stats.currentPopulation <= 0) {
      this.events.emit(GameEventTypes.COLONY_EXTINCT, {
        finalPopulation: 0,
        lastSurvivor: ant.getDisplayName(),
      }, this.id);
    }
  }

  /**
   * File a headline.
   *
   * Stamped with the colony's own clock. It used to be `performance.now()`, which
   * `docs/determinism.md` listed as the last ambient clock left in a simulation
   * path and which nothing rendered — the client shows the message and the type.
   * The surface's headlines moved to sim-time in ADR-003; these are the same thing
   * one layer down, and they had to move for a snapshot to be idempotent: two
   * restored worlds agreed on every ant and disagreed about what time it was when
   * the queen was fed.
   */
  private addNews(message: string, type: ColonyNewsItem['type']): void {
    this.news.push({
      message,
      timestamp: this.gameTimeMs,
      type,
    });

    // Keep only last 50 news items
    if (this.news.length > 50) {
      this.news.shift();
    }
  }
  update(dt: number): void {
    this.tickCount++;
    this.gameTimeMs += dt;  // Track total elapsed time in milliseconds
    // Colony age is displayed in days of 3,600 reference ticks, so it counts
    // sim-time rather than update() calls — otherwise the colony ages ten times
    // more slowly at the canonical 6 Hz cadence than it does at 60.
    this.stats.colonyAge += refTicks(dt);

    // Cleanup stale entries in antsOnSurface (ants that died or no longer exist)
    for (const antId of this.antsOnSurface) {
      const ant = this.ants.get(antId);
      if (!ant || !ant.isAlive()) {
        this.antsOnSurface.delete(antId);
  
      }
    }

    // Update debris particles from damage events
    this.updateDebrisParticles(dt);

    // Update alert state
    if (this.alertState) {
      this.alertTimer -= dt;
      if (this.alertTimer <= 0) {
        this.alertState = false;
      }
    }

    // Update scent-based navigation system (diffusion, decay, emission)
    this.tunnelNetwork.updateScents(dt);

    // Decay pheromones periodically (legacy)
    const pheromoneDecay = advanceTimer(
      this.pheromoneDecayTimer, dt, this.colonyConfig.PHEROMONE_DECAY_INTERVAL
    );
    this.pheromoneDecayTimer = pheromoneDecay.value;
    for (let i = 0; i < pheromoneDecay.fires; i++) {
      this.tunnelNetwork.decayPheromones(this.colonyConfig.PHEROMONE_DECAY_RATE);
    }

    // Colony metabolism - all ants consume food
    this.metabolismSystem.update(dt, this.getMetabolismContext());

    // Update chamber stimuli for response-threshold model.
    // Fired once per elapsed interval rather than once per tick: the update
    // decays existing stimuli, so skipping a fire slows the decay rather than
    // merely delaying a recompute.
    const stimuli = advanceTimer(this.stimuliTimer, dt, this.stimConfig.STIMULI_UPDATE_INTERVAL);
    this.stimuliTimer = stimuli.value;
    for (let i = 0; i < stimuli.fires; i++) {
      this.updateChamberStimuli();
    }

    // Check for dynamic role transitions based on local observations
    const roleTransitionContext: RoleTransitionContext = {
      ants: this.ants,
      antsOnSurface: this.antsOnSurface,
      gameTimeMs: this.gameTimeMs,
      alertState: this.alertState,
      getForagingUrgency: () => this.getForagingUrgency(),
      getHungryLarvaeCount: () => this.getHungryLarvae().length,
      addNews: (msg, type) => this.addNews(msg, type),
      getChamberAt: (pos) => this.tunnelNetwork.getChamberAt(pos),
      getAntsNearby: (pos, radius) => this.getAntsNearPosition(pos, radius),
    };
    const transitions = this.roleTransitionSystem.update(dt, roleTransitionContext);
    for (const ant of transitions) {
      this.addNews(`${ant.getDisplayName()} became a ${ant.role}`, 'info');
    }

    // Update queen hunger and egg laying
    if (this.queen && this.queen.isAlive()) {
      this.queenHunger += (dt / 1000) * this.colonyConfig.QUEEN_HUNGER_RATE;
      if (this.queenHunger >= this.colonyConfig.QUEEN_HUNGER_MAX) {
        this.queen.health -= (dt / 1000) * 2; // Slow starvation damage
        if (this.queen.health <= 0) {
          this.handleQueenDeath();
        }
      }

      // Queen lays eggs when not too hungry
      if (this.queenHunger < 50) {
        const interval = this.broodConfig.EGG_LAY_INTERVAL;
        this.eggLayTimer += dt;
        if (this.eggLayTimer >= interval) {
          if (this.brood.size < this.broodConfig.MAX_BROOD) {
            // Carry the remainder rather than zeroing, so the laying period is
            // the configured interval and not the interval rounded up to a tick.
            this.eggLayTimer -= interval;
            this.layEgg();
          } else {
            // Nursery full. Hold at one interval of credit so the queen lays as
            // soon as room appears, without banking a burst for every interval
            // she spent waiting — which is what an uncapped accumulator would do.
            this.eggLayTimer = interval;
          }
        }
      }
    }

    // Update brood (eggs, larvae, pupae)
    this.updateBrood(dt);

    // Handle queen replacement process
    this.updateQueenReplacement(dt);

    // Update each ant
    for (const ant of this.ants.values()) {
      if (!ant.isAlive()) continue;
      if (this.antsOnSurface.has(ant.id)) continue; // Skip ants on surface

      // Age. Lifespans are authored in reference ticks, so ageing tracks
      // sim-time and an ant lives the same span at any cadence.
      ant.age += refTicks(dt);

      // Natural death from old age
      if (ant.age >= ant.maxAge) {
        this.killAnt(ant, 'old age');
        continue;
      }

      // Starvation check - ants die when hunger reaches max (queen has separate starvation via queenHunger)
      if (ant.role !== AntRole.QUEEN && ant.hunger >= this.colonyConfig.ANT_HUNGER_MAX) {
        this.killAnt(ant, 'starvation');
        continue;
      }

      // Track chamber transitions - all ants get a brief "thinking" pause when entering new chamber
      const currentChamber = ant.currentChamberId !== null
        ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
        : this.tunnelNetwork.getChamberAt(ant.position);
      if (currentChamber && currentChamber.id !== ant.lastChamberChangeId) {
        ant.lastChamberChangeId = currentChamber.id;
        ant.chamberEntryTime = this.gameTimeMs;
        // Reset interaction state on chamber change
        if (ant.interactionTargetId !== null) {
          ant.interactionTargetId = null;
          ant.targetPosition = null;
        }
      }

      // Update based on role
      switch (ant.role) {
        case AntRole.FORAGER:
          this.updateForager(ant, dt);
          break;
        case AntRole.NURSE:
          this.updateNurse(ant, dt);
          break;
        case AntRole.SOLDIER:
          this.updateSoldier(ant, dt);
          break;
        case AntRole.ENGINEER:
          this.updateEngineer(ant, dt);
          break;
        case AntRole.QUEEN:
          this.updateQueen(ant, dt);
          break;
      }

      // Philosopher ant occasionally contemplates (but not during critical tasks)
      const isCriticalTask = ant.task === AntTask.REPAIRING ||
                             ant.task === AntTask.CARRYING_FOOD ||
                             ant.task === AntTask.CARRYING_EGG;
      if (ant.isPhilosopher && !isCriticalTask && rollPerRefTick(this.rng, 0.001, dt)) {
        ant.state = AntState.CONTEMPLATING;
        ant.targetPosition = null;
        ant.task = AntTask.NONE;
      }
    }
  }

  // ======== Metabolism Methods (delegated to MetabolismSystem) ========

  private getMetabolismContext(): MetabolismContext {
    return {
      tunnelNetwork: this.tunnelNetwork,
      ants: this.ants,
      antsOnSurface: this.antsOnSurface,
      queen: this.queen,
      queenHunger: this.queenHunger,
      killAnt: (ant, cause) => this.killAnt(ant, cause),
    };
  }

  /**
   * Update chamber stimuli based on current colony state.
   * This drives the response-threshold model for task allocation.
   */
  private updateChamberStimuli(): void {
    const chambers = this.tunnelNetwork.getAllChambers();
    const totalFood = this.tunnelNetwork.getTotalFood();
    const maxCapacity = this.tunnelNetwork.getFoodStorages().filter(s => !s.destroyed).length * 1000;
    const foodRatio = maxCapacity > 0 ? totalFood / maxCapacity : 0;
    const collapsedTunnels = this.tunnelNetwork.getCollapsedTunnels();

    // Count hungry larvae globally
    let totalHungryLarvae = 0;
    let totalEggsInQueenChamber = 0;

    for (const brood of this.brood.values()) {
      if (brood.stage === BroodStage.LARVA && brood.fed < this.broodConfig.LARVA_FOOD_NEEDED) {
        totalHungryLarvae++;
      }
      if (brood.stage === BroodStage.EGG) {
        const queenChamber = this.tunnelNetwork.getQueenChamber();
        if (queenChamber && brood.chamberId === queenChamber.id) {
          totalEggsInQueenChamber++;
        }
      }
    }

    for (const chamber of chambers) {
      if (chamber.destroyed) continue;

      const stimuli = chamber.stimuli;

      // First decay all existing stimuli. `STIMULUS_KEYS` rather than
      // `Object.keys(stimuli)`: the fields are what decays, not whatever order
      // the literal that built this chamber assigned them in (rule 4).
      for (const key of STIMULUS_KEYS) {
        stimuli[key] = Math.max(0, stimuli[key] - this.stimConfig.STIMULUS_DECAY_RATE);
      }

      // Now add stimuli based on chamber type and state

      // NURSERY: brood hunger, brood risk, eggs present
      if (chamber.type === ChamberType.NURSERY) {
        // Count hungry larvae in this chamber
        let hungryLarvaeHere = 0;
        for (const brood of this.brood.values()) {
          if (brood.chamberId === chamber.id && brood.stage === BroodStage.LARVA) {
            if (brood.fed < this.broodConfig.LARVA_FOOD_NEEDED) {
              hungryLarvaeHere++;
            }
          }
        }
        stimuli.broodHunger = Math.min(1, hungryLarvaeHere * 0.15);
        stimuli.broodRisk = (1 - chamber.climateQuality) * 0.5 + (1 - chamber.cleanliness) * 0.3;
      }

      // QUEEN CHAMBER: queen hunger, eggs present
      if (chamber.type === ChamberType.QUEEN_CHAMBER) {
        stimuli.queenHunger = Math.min(1, this.queenHunger / this.colonyConfig.QUEEN_HUNGER_MAX);
        stimuli.eggPresent = Math.min(1, totalEggsInQueenChamber * 0.2);
      }

      // ENTRANCE: alarm level, food incoming (when foragers return)
      if (chamber.type === ChamberType.ENTRANCE) {
        stimuli.alarmLevel = this.alertState ? 0.8 : chamber.riskLevel * 0.3;
        // foodIncoming rises when foragers are returning (handled elsewhere)
      }

      // FOOD_STORAGE: food low stimulus
      if (chamber.type === ChamberType.FOOD_STORAGE) {
        stimuli.foodLow = Math.max(0, 1 - foodRatio);
      }

      // MIDDEN: waste level
      if (chamber.type === ChamberType.MIDDEN) {
        stimuli.wasteLevel = 1 - chamber.cleanliness;
      }

      // All chambers: damage level from nearby collapsed tunnels
      for (const tunnel of collapsedTunnels) {
        const midX = (tunnel.start.x + tunnel.end.x) / 2;
        const midY = (tunnel.start.y + tunnel.end.y) / 2;
        const dist = Math.sqrt(
          Math.pow(chamber.position.x - midX, 2) +
          Math.pow(chamber.position.y - midY, 2)
        );
        if (dist < 100) {
          stimuli.damageLevel = Math.min(1, stimuli.damageLevel + (1 - dist / 100) * 0.5);
        }
      }

      // Global alarm propagates to all chambers (decays with depth)
      if (this.alertState) {
        const alarmStrength = Math.max(0, 0.6 - chamber.depth * 0.1);
        stimuli.alarmLevel = Math.max(stimuli.alarmLevel, alarmStrength);
      }
    }

    // Propagate brood hunger to ALL chambers (so nurses can sense it from anywhere)
    const nursery = this.tunnelNetwork.getNursery();
    if (nursery && !nursery.destroyed) {
      const broodHunger = nursery.stimuli.broodHunger;
      if (broodHunger > 0) {
        for (const chamber of this.tunnelNetwork.getAllChambers()) {
          if (chamber.destroyed || chamber.id === nursery.id) continue;
          // Propagate at 60% strength
          chamber.stimuli.broodHunger = Math.max(
            chamber.stimuli.broodHunger,
            broodHunger * 0.6
          );
        }
      }
    }

    // Propagate queen hunger to ALL chambers (nurses need to know from anywhere)
    const queenChamber = this.tunnelNetwork.getQueenChamber();
    if (queenChamber && !queenChamber.destroyed) {
      const queenHunger = queenChamber.stimuli.queenHunger;
      if (queenHunger > 0) {
        for (const chamber of this.tunnelNetwork.getAllChambers()) {
          if (chamber.destroyed || chamber.id === queenChamber.id) continue;
          // Propagate at 50% strength
          chamber.stimuli.queenHunger = Math.max(
            chamber.stimuli.queenHunger,
            queenHunger * 0.5
          );
        }
      }

      // Propagate egg presence to ALL chambers (nurses need to know about eggs)
      const eggPresent = queenChamber.stimuli.eggPresent;
      if (eggPresent > 0) {
        for (const chamber of this.tunnelNetwork.getAllChambers()) {
          if (chamber.destroyed || chamber.id === queenChamber.id) continue;
          // Propagate at 70% strength - eggs are important!
          chamber.stimuli.eggPresent = Math.max(
            chamber.stimuli.eggPresent,
            eggPresent * 0.7
          );
        }
      }
    }
  }

  /**
   * Calculate the desirability of a task for an ant using response-threshold model.
   * D(task) = stimulus - threshold + noise - switching_cost_if_different_task
   * Returns a value where higher = more desirable. Negative means "don't do it".
   */
  private calculateTaskDesirability(
    ant: Ant,
    taskType: TaskType,
    stimulus: number,
    isCurrentTask: boolean
  ): number {
    const threshold = ant.getEffectiveThreshold(taskType);
    const noise = (this.rng.next() - 0.5) * 0.1; // Small noise ±0.05
    const switchingCost = isCurrentTask ? 0 : this.stimConfig.SWITCHING_COST;

    // D = S - θ + noise - switching_cost
    const desirability = stimulus - threshold + noise - switchingCost;

    return desirability;
  }

  /**
   * Get the stimulus value for a task type based on the ant's current chamber.
   * Maps TaskType to the relevant chamber stimulus.
   */
  private getStimulusForTask(ant: Ant, taskType: TaskType): number {
    const chamber = this.tunnelNetwork.getChamberAt(ant.position);
    if (!chamber) return this.stimConfig.BASELINE_ACTIVITY;

    const stimuli = chamber.stimuli;

    // Each task has a baseline activity level plus situational stimulus
    // Baseline must be high enough to overcome threshold + switching cost
    // For primary role tasks: baseline alone should give positive desirability
    switch (taskType) {
      case TaskType.FORAGE: {
        // Foraging desirability based on colony food smell (biologically plausible)
        // When food smell is high, foragers sense abundance and stay home
        const urgency = this.getForagingUrgency();
        if (urgency < 0) {
          // Negative urgency = food smell is strong, stay home
          // Very low desirability - foragers will rest instead
          return this.stimConfig.BASELINE_ACTIVITY * 0.3;
        }
        // Normal: baseline + urgency boost when colony food is low
        return this.stimConfig.BASELINE_ACTIVITY * 1.5 + urgency * 1.0;
      }

      case TaskType.FEED_LARVAE:
        // Nursing larvae: strong baseline + hunger stimulus
        // Nurses should actively seek hungry larvae
        return this.stimConfig.BASELINE_ACTIVITY * 1.5 + stimuli.broodHunger * 1.0;

      case TaskType.FEED_QUEEN:
        // Queen care: strong baseline + hunger stimulus
        return this.stimConfig.BASELINE_ACTIVITY * 1.3 + stimuli.queenHunger * 1.0;

      case TaskType.CARRY_EGG:
        // Egg transport: moderate baseline + egg presence (eggs are priority when present)
        return this.stimConfig.BASELINE_ACTIVITY * 1.0 + stimuli.eggPresent * 1.5;

      case TaskType.TRANSPORT:
        // Transport: moderate baseline + food shortage
        return this.stimConfig.BASELINE_ACTIVITY * 0.6 + stimuli.foodLow * 0.6;

      case TaskType.GUARD:
        // Guarding: strong baseline (soldiers always somewhat vigilant) + alarm
        return this.stimConfig.BASELINE_ACTIVITY * 1.4 + stimuli.alarmLevel * 0.8;

      case TaskType.PATROL:
        // Patrol: soldier/engineer default activity when no immediate threats
        return this.stimConfig.BASELINE_ACTIVITY * 1.2 + stimuli.alarmLevel * 0.4;

      case TaskType.REPAIR:
        // Repair: moderate baseline + strong damage response
        return this.stimConfig.BASELINE_ACTIVITY * 0.8 + stimuli.damageLevel * 1.5;

      case TaskType.CLEAN:
        // Cleaning: moderate baseline + waste level
        return this.stimConfig.BASELINE_ACTIVITY * 0.6 + stimuli.wasteLevel * 0.5 + (1 - chamber.cleanliness) * 0.3;

      case TaskType.REST:
        // Rest: low baseline, mainly fatigue-driven
        return this.stimConfig.BASELINE_ACTIVITY * 0.2 + ant.fatigue / 100 * 0.8;

      default:
        return this.stimConfig.BASELINE_ACTIVITY;
    }
  }

  /**
   * Select the best task for an ant using response-threshold model.
   * Returns the TaskType with highest positive desirability, or null if none.
   */
  private selectBestTask(ant: Ant, relevantTasks: TaskType[]): TaskType | null {
    let bestTask: TaskType | null = null;
    let bestDesirability = 0; // Must be positive to be chosen

    // Map current AntTask to TaskType for switching cost calculation
    const currentTaskType = this.antTaskToTaskType(ant.task);

    for (const taskType of relevantTasks) {
      const stimulus = this.getStimulusForTask(ant, taskType);
      const isCurrentTask = taskType === currentTaskType;
      const desirability = this.calculateTaskDesirability(ant, taskType, stimulus, isCurrentTask);

      // Track for debugging
      if (taskType === currentTaskType) {
        ant.currentStimulusResponse = desirability;
      }

      if (desirability > bestDesirability) {
        bestDesirability = desirability;
        bestTask = taskType;
      }
    }

    return bestTask;
  }

  /**
   * Map AntTask enum to TaskType enum for response-threshold model.
   */
  private antTaskToTaskType(task: AntTask): TaskType | null {
    switch (task) {
      case AntTask.ON_SURFACE:
      case AntTask.CARRYING_FOOD:
        return TaskType.FORAGE;

      case AntTask.CARRYING_EGG:
        return TaskType.CARRY_EGG;

      case AntTask.GUARDING:
        return TaskType.GUARD;

      case AntTask.PATROLLING:
        return TaskType.PATROL;

      case AntTask.REPAIRING:
        return TaskType.REPAIR;

      default:
        return null;
    }
  }

  /**
   * Calculate how urgently the colony needs foragers to go out.
   */
  private getForagingUrgency(): number {
    return this.metabolismSystem.getForagingUrgency(this.getMetabolismContext());
  }

  /**
   * Count brood (eggs, larvae, pupae) in a specific chamber.
   */
  private countBroodInChamber(chamberId: number): number {
    let count = 0;
    for (const brood of this.brood.values()) {
      if (brood.chamberId === chamberId && brood.carriedByAntId === null) {
        count++;
      }
    }
    return count;
  }

  /**
   * Find a suitable chamber to drop food, considering capacity and resource repulsion.
   * Returns null if no suitable chamber found.
   */
  private findChamberForFood(preferredChamber: Chamber, amount: number): Chamber | null {
    const chambers = this.tunnelNetwork.getAllChambers();
    const validChambers: Chamber[] = [];

    for (const chamber of chambers) {
      if (chamber.destroyed) continue;
      if (chamber.type === ChamberType.ENTRANCE) continue; // Don't store food at entrances
      if (chamber.type === ChamberType.MIDDEN) continue; // Don't store food in midden
      if (chamber.type === ChamberType.QUEEN_CHAMBER) continue; // Don't clutter queen's chamber

      if (chamber.foodStored + amount > chamber.foodCapacity) continue; // Would overflow

      validChambers.push(chamber);
    }

    if (validChambers.length === 0) return null;

    // Check preferred chamber first
    if (validChambers.includes(preferredChamber)) {
      if (preferredChamber.foodStored + amount <= preferredChamber.foodCapacity) {
        const broodCount = this.countBroodInChamber(preferredChamber.id);
        // Check egg repulsion: if eggs present, small chance to use anyway
        if (broodCount > 0) {
          // 10% chance to drop here anyway if both resources present
          if (this.rng.chance(0.1)) return preferredChamber;
        } else {
          return preferredChamber;
        }
      }
    }

    // Find chambers without eggs (repulsion) or accept with low probability
    const noEggChambers = validChambers.filter(c => this.countBroodInChamber(c.id) === 0);
    if (noEggChambers.length > 0) {
      return this.rng.pick(noEggChambers)!;
    }

    // All chambers have eggs - pick one with 10% probability each, otherwise fail
    if (this.rng.chance(0.1)) {
      return this.rng.pick(validChambers)!;
    }

    return null;
  }

  /**
   * Queen lays an egg in her chamber.
   */
  private layEgg(): void {
    if (!this.queen || !this.queen.isAlive()) return;

    const queenChamber = this.tunnelNetwork.getQueenChamber();
    if (!queenChamber || queenChamber.destroyed) return;

    // Queen always lays in her chamber (nurses will move eggs if needed)
    // Create egg near queen with slight random offset
    const angle = this.rng.next() * Math.PI * 2;
    const dist = this.rng.next() * 15;
    const eggPos: Position = {
      x: this.queen.position.x + Math.cos(angle) * dist,
      y: this.queen.position.y + Math.sin(angle) * dist,
    };

    const egg: Brood = {
      id: this.nextBroodId++,
      stage: BroodStage.EGG,
      position: eggPos,
      chamberId: queenChamber.id,
      age: 0,
      fed: 0,
      carriedByAntId: null,
      droppedTime: 0,  // Queen-laid eggs can be picked up immediately
    };

    this.brood.set(egg.id, egg);
    queenChamber.eggCount++;
    this.queen.stats.eggsNursed++; // Track eggs laid
  }

  /**
   * Update all brood (eggs develop to larvae, larvae to pupae, pupae to ants).
   */
  private updateBrood(dt: number): void {
    const nursery = this.tunnelNetwork.getNursery();

    for (const brood of this.brood.values()) {
      // Skip if being carried
      if (brood.carriedByAntId !== null) continue;

      brood.age += dt;

      switch (brood.stage) {
        case BroodStage.EGG:
          // Eggs develop into larvae after time
          if (brood.age >= this.broodConfig.EGG_DURATION) {
            brood.stage = BroodStage.LARVA;
            brood.age = 0;
            brood.fed = 0;
          }
          break;

        case BroodStage.LARVA:
          // Larvae need feeding to develop
          // They develop when fed enough AND time has passed
          if (brood.fed >= this.broodConfig.LARVA_FOOD_NEEDED && brood.age >= this.broodConfig.LARVA_DURATION) {
            brood.stage = BroodStage.PUPA;
            brood.age = 0;
          }
          // Larvae die if not fed for too long (3x normal duration)
          if (brood.age >= this.broodConfig.LARVA_DURATION * 3 && brood.fed < this.broodConfig.LARVA_FOOD_NEEDED * 0.5) {
            const chamber = this.tunnelNetwork.getChamber(brood.chamberId);
            if (chamber) chamber.eggCount = Math.max(0, chamber.eggCount - 1);
            this.brood.delete(brood.id);
          }
          break;

        case BroodStage.PUPA:
          // Pupae emerge as adult ants
          if (brood.age >= this.broodConfig.PUPA_DURATION) {
            const chamber = this.tunnelNetwork.getChamber(brood.chamberId);
            if (chamber) chamber.eggCount = Math.max(0, chamber.eggCount - 1);
            this.emergePupa(brood);
            this.brood.delete(brood.id);
          }
          break;
      }
    }
  }

  /**
   * A pupa emerges as an adult ant.
   */
  private emergePupa(brood: Brood): void {
    // Pick role based on colony needs
    const foragerCount = Array.from(this.ants.values()).filter(a => a.role === AntRole.FORAGER && a.isAlive()).length;
    const nurseCount = Array.from(this.ants.values()).filter(a => a.role === AntRole.NURSE && a.isAlive()).length;
    const soldierCount = Array.from(this.ants.values()).filter(a => a.role === AntRole.SOLDIER && a.isAlive()).length;
    const engineerCount = Array.from(this.ants.values()).filter(a => a.role === AntRole.ENGINEER && a.isAlive()).length;

    // Determine role based on proportions (favor what's lacking)
    let role: AntRole;
    const total = foragerCount + nurseCount + soldierCount + engineerCount;
    if (total === 0) {
      role = AntRole.FORAGER;
    } else {
      const foragerRatio = foragerCount / total;
      const nurseRatio = nurseCount / total;
      const soldierRatio = soldierCount / total;

      // Target ratios: 50% forager, 17% nurse, 15% soldier, 18% engineer
      // Nurses are efficient - they rest between tasks and only respond to hunger signals
      if (foragerRatio < 0.45) role = AntRole.FORAGER;
      else if (nurseRatio < 0.17) role = AntRole.NURSE;
      else if (soldierRatio < 0.12) role = AntRole.SOLDIER;
      else if (this.rng.chance(0.8)) role = AntRole.FORAGER; // Prefer forager over engineer
      else role = AntRole.ENGINEER;
    }

    const newAnt = this.createAnt(role, brood.position);
    this.stats.births++;
    this.stats.currentPopulation++;
    if (this.stats.currentPopulation > this.stats.peakPopulation) {
      this.stats.peakPopulation = this.stats.currentPopulation;
    }

    this.addNews(`A new ${role} has emerged: ${newAnt.name}`, 'info');
  }

  /**
   * Get eggs in queen chamber that need to be carried to nursery.
   */
  private getEggsInQueenChamber(): Brood[] {
    const queenChamber = this.tunnelNetwork.getQueenChamber();
    if (!queenChamber) return [];

    return Array.from(this.brood.values()).filter(
      b => b.stage === BroodStage.EGG &&
           b.chamberId === queenChamber.id &&
           b.carriedByAntId === null
    );
  }

  /**
   * Get larvae in nursery that need feeding.
   */
  private getHungryLarvae(): Brood[] {
    const nursery = this.tunnelNetwork.getNursery();
    if (!nursery) return [];

    return Array.from(this.brood.values()).filter(
      b => b.stage === BroodStage.LARVA &&
           b.chamberId === nursery.id &&
           b.fed < this.broodConfig.LARVA_FOOD_NEEDED &&
           b.carriedByAntId === null
    );
  }

  /**
   * Get ants within a radius of a position.
   * Used for local observation in role transition decisions.
   */
  private getAntsNearPosition(pos: Position, radius: number): Ant[] {
    const radiusSq = radius * radius;
    return Array.from(this.ants.values()).filter(ant => {
      if (!ant.isAlive()) return false;
      if (this.antsOnSurface.has(ant.id)) return false;
      const dx = ant.position.x - pos.x;
      const dy = ant.position.y - pos.y;
      return dx * dx + dy * dy <= radiusSq;
    });
  }

  /**
   * Update forager behavior.
   */
  private updateForager(ant: Ant, dt: number): void {
    ant.taskTimer += dt;

    // Get current chamber for local sensing
    const currentChamber = ant.currentChamberId !== null
      ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
      : this.tunnelNetwork.getChamberAt(ant.position);

    switch (ant.task) {
      case AntTask.NONE:
        // If carrying food, deliver it
        if (ant.carryingFood) {
          ant.task = AntTask.CARRYING_FOOD;
          this.setPathToStorage(ant);
          break;
        }

        // Check foraging urgency (based on food scent levels)
        const urgency = this.getForagingUrgency();
        if (urgency < 0) {
          // Colony has plenty of food - rest/wander
          this.wanderAnt(ant, dt);
          break;
        }

        // At entrance? Try to exit to surface
        if (currentChamber && currentChamber.type === ChamberType.ENTRANCE) {
          if (!this.tryExitToSurface(ant)) {
            // Exit failed - wander away so we don't get stuck here
            this.wanderAnt(ant, dt);
          }
          break;
        }

        // Head toward entrance to go foraging
        this.setPathToEntrance(ant);
        if (this.followScent(ant, dt)) {
          // Arrived at scent destination but not at entrance - keep wandering
          this.wanderAnt(ant, dt);
        }
        break;

      case AntTask.CARRYING_FOOD:
        // Try to deposit food in current chamber
        if (currentChamber && this.depositFood(ant)) {
          ant.task = AntTask.NONE;
          ant.carryingFood = false;
          ant.carriedFoodAmount = 0;
          ant.stats.foragingTrips++;
          break;
        }
        // Keep following scent to find storage
        if (this.followScent(ant, dt)) {
          // Arrived but couldn't deposit - pick new destination
          this.setPathToStorage(ant);
          this.wanderAnt(ant, dt);
        }
        break;

      case AntTask.ON_SURFACE:
        // Surface behavior handled by surface layer
        break;

      default:
        ant.task = AntTask.NONE;
        break;
    }
  }

  /**
   * Update nurse behavior.
   * Nurses: carry eggs to nursery, feed larvae, feed queen.
   */
  private updateNurse(ant: Ant, dt: number): void {
    ant.taskTimer += dt;

    // LOCAL SENSING: Check what's in the current chamber
    const currentChamber = ant.currentChamberId !== null
      ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
      : this.tunnelNetwork.getChamberAt(ant.position);

    // Clean up expired avoid entries
    for (const [chamberId, until] of ant.avoidChambersUntil) {
      if (this.gameTimeMs > until) {
        ant.avoidChambersUntil.delete(chamberId);
      }
    }

    // CHAMBER ENTRY DELAY: Wait 400ms before taking action in a new chamber
    const CHAMBER_SETTLE_TIME = 400;
    const timeInChamber = this.gameTimeMs - ant.chamberEntryTime;
    const hasSettled = timeInChamber > CHAMBER_SETTLE_TIME;

    switch (ant.task) {
      case AntTask.NONE:
        // REACTIVE BEHAVIOR: React to what ant is carrying or sensing locally

        // 0. If approaching an egg, continue moving toward it
        if (ant.interactionTargetId !== null) {
          const targetBrood = this.brood.get(ant.interactionTargetId);
          if (targetBrood && targetBrood.carriedByAntId === null && targetBrood.position) {
            // Move toward the egg
            this.moveTowardTarget(ant, dt);

            // Check if close enough to pick up (within 12 units)
            const dist = Math.hypot(
              ant.position.x - targetBrood.position.x,
              ant.position.y - targetBrood.position.y
            );
            if (dist < 12) {
              // Pick up the egg
              if (currentChamber) {
                currentChamber.eggCount = Math.max(0, currentChamber.eggCount - 1);
                // Remember where we picked it up - don't drop in same chamber
                ant.pickupChamberId = currentChamber.id;
              }
              targetBrood.carriedByAntId = ant.id;
              ant.carryingEgg = true;
              ant.task = AntTask.CARRYING_EGG;
              ant.interactionTargetId = null;
              ant.targetPosition = null;
              this.setPathToNursery(ant);
            }
            break;
          } else {
            // Target no longer valid (picked up by another ant or disappeared)
            ant.interactionTargetId = null;
            ant.targetPosition = null;
          }
        }

        // 1. If carrying egg, transport it to nursery
        if (ant.carryingEgg) {
          ant.task = AntTask.CARRYING_EGG;
          this.setPathToNursery(ant);
          break;
        }

        // 2. If carrying food, decide where to take it based on local scent
        if (ant.carryingFood) {
          // Follow strongest need scent (BROOD or QUEEN)
          const broodScent = currentChamber?.scents?.[ScentType.BROOD_SCENT] ?? 0;
          const queenScent = currentChamber?.scents?.[ScentType.QUEEN_SCENT] ?? 0;
          ant.task = AntTask.CARRYING_FOOD;
          if (broodScent > queenScent) {
            this.setPathToNursery(ant);
          } else {
            this.setPathToQueen(ant);
          }
          break;
        }

        // 3. LOCAL REACTION: Check current chamber for things to do
        // Only act after settling into chamber
        if (currentChamber && hasSettled) {
          // Check if we already decided not to pick up eggs from this chamber recently
          const eggCheckCooldown = ant.avoidChambersUntil.get(currentChamber.id);
          const onEggCooldown = eggCheckCooldown !== undefined && this.gameTimeMs < eggCheckCooldown;

          // REST PERIOD: After completing a task, nurses rest before seeking new work
          // taskTimer tracks time since last task completion
          // Biologically plausible: ants don't work constantly, they rest between tasks
          const REST_PERIOD = 5000; // 5 seconds of rest after completing a task
          const isResting = ant.taskTimer < REST_PERIOD;

          // SENSE HUNGER: Check if colony has hungry larvae (broodHunger stimulus)
          // This propagates from chambers with hungry larvae
          const broodHunger = currentChamber.stimuli?.broodHunger ?? 0;
          const queenHunger = currentChamber.stimuli?.queenHunger ?? 0;
          const hasHungerSignal = broodHunger > 0.1 || queenHunger > 0.3;

          // PRIORITY 1: Pick up food ONLY if there's a hunger signal
          // Biologically plausible: nurses respond to pheromones from hungry larvae
          const foodHere = currentChamber.foodStored ?? 0;
          const broodScent = currentChamber.scents?.[ScentType.BROOD_SCENT] ?? 0;
          const queenScent = currentChamber.scents?.[ScentType.QUEEN_SCENT] ?? 0;

          if (!isResting && foodHere > 0 && hasHungerSignal) {
            // Grab some food to deliver
            const amount = Math.min(20, foodHere);
            currentChamber.foodStored = foodHere - amount;
            ant.carryingFood = true;
            ant.carriedFoodAmount = amount;
            ant.carriedFoodHue = currentChamber.foodHue ?? 20;
            ant.task = AntTask.CARRYING_FOOD;
            // Decide destination based on scent
            if (broodScent > queenScent) {
              this.setPathToNursery(ant);
            } else {
              this.setPathToQueen(ant);
            }
            break;
          }

          // Found hungry larvae here? Feed them directly!
          const larvaeHere = Array.from(this.brood.values()).filter(
            b => b.stage === BroodStage.LARVA &&
                 b.chamberId === currentChamber.id &&
                 b.fed < this.broodConfig.LARVA_FOOD_NEEDED
          );
          if (larvaeHere.length > 0 && ant.carryingFood) {
            // Feed the larva
            const larva = larvaeHere[0];
            const feedAmount = Math.min(ant.carriedFoodAmount, this.broodConfig.LARVA_FOOD_NEEDED - larva.fed);
            larva.fed += feedAmount;
            ant.carriedFoodAmount -= feedAmount;
            if (ant.carriedFoodAmount <= 0) {
              ant.carryingFood = false;
              ant.carriedFoodAmount = 0;
            }
            ant.stats.eggsNursed++;
            break;
          }

          // PRIORITY 2: Found eggs? Maybe pick one up (low probability)
          // Skip if resting - don't pick up eggs during rest period
          // Only pick settled eggs (3 second cooldown after being dropped)
          const EGG_SETTLE_TIME = 3000;
          const eggsHere = (isResting || onEggCooldown) ? [] : Array.from(this.brood.values()).filter(
            b => b.stage === BroodStage.EGG &&
                 b.chamberId === currentChamber.id &&
                 b.carriedByAntId === null &&
                 (this.gameTimeMs - (b.droppedTime ?? 0)) > EGG_SETTLE_TIME
          );
          if (eggsHere.length > 0) {
            // More eggs = less likely to move them (eggs like company)
            // Unless queen is here - then always move eggs away
            const queenInChamber = this.queen &&
              this.tunnelNetwork.getChamberAt(this.queen.position)?.id === currentChamber.id;

            // Probability to pick up: very low to encourage strong egg clustering
            // 1 egg: 6%, 2 eggs: 3%, 3 eggs: 2%, 4+ eggs: ~1%
            // Skip this check if queen is in chamber (always move eggs away from queen)
            let shouldPickUp = queenInChamber; // Always pick up if queen present
            if (!queenInChamber) {
              const pickupChance = 0.06 / eggsHere.length;
              if (this.rng.chance(pickupChance)) {
                shouldPickUp = true;
              } else {
                // Don't pick up - eggs are happy together
                // Add cooldown so we don't re-check this chamber for 10 seconds
                ant.avoidChambersUntil.set(currentChamber.id, this.gameTimeMs + 10000);
                // DON'T break here - let the ant continue to patrol
              }
            }

            if (shouldPickUp) {
              // Find closest egg not being targeted by another ant
              const availableEggs = eggsHere.filter(egg =>
                !Array.from(this.ants.values()).some(
                  a => a.id !== ant.id && a.interactionTargetId === egg.id
                )
              );
              if (availableEggs.length > 0) {
                // Pick closest egg
                let closestEgg = availableEggs[0];
                let closestDist = Infinity;
                for (const egg of availableEggs) {
                  if (egg.position) {
                    const d = Math.hypot(
                      ant.position.x - egg.position.x,
                      ant.position.y - egg.position.y
                    );
                    if (d < closestDist) {
                      closestDist = d;
                      closestEgg = egg;
                    }
                  }
                }
                // Set target and start moving toward egg
                ant.interactionTargetId = closestEgg.id;
                ant.targetPosition = closestEgg.position ? { ...closestEgg.position } : null;
                break; // Will move toward it next frame
              }
            }
          }
        }

        // 4. PATROLLING: Wander the colony following NEST_ODOR
        // Biologically plausible: when not responding to stimuli, ants patrol
        // They move through the colony, ready to respond when they sense needs
        // Occasionally check for brood scent or food smell to see if there's work
        const rand = this.rng.next();
        if (rand < 0.6) {
          this.setScent(ant, ScentType.NEST_ODOR);    // Patrol colony (most common)
        } else if (rand < 0.8) {
          this.setScent(ant, ScentType.BROOD_SCENT); // Check brood area
        } else {
          this.setScent(ant, ScentType.FOOD_SMELL);  // Check food storage
        }
        if (this.followScent(ant, dt)) {
          // Arrived - keep patrolling
          this.wanderAnt(ant, dt);
        }
        break;

      case AntTask.CARRYING_EGG:
        // LOCAL: Check current chamber - can we drop egg here?
        // Biologically plausible: ants cluster brood together (eggs + larvae)
        // The longer they carry, the more eager they become to drop
        {
          const chamber = ant.currentChamberId !== null
            ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
            : this.tunnelNetwork.getChamberAt(ant.position);

          // Wait for settling before attempting to drop
          const timeInChamberEgg = this.gameTimeMs - ant.chamberEntryTime;
          const hasSettledEgg = timeInChamberEgg > 300;

          // Carry fatigue: the longer we carry, the more willing to drop
          // < 3s: only drop in ideal chambers (with brood)
          // 3-10s: drop in any suitable chamber
          // > 10s: very eager, ignore most restrictions
          const carryTime = ant.taskTimer;
          const isEager = carryTime > 3000;
          const isVeryEager = carryTime > 10000;

          if (chamber && hasSettledEgg) {
            // Check if queen is in this chamber - never drop eggs where queen is
            const queenInChamber = this.queen && this.queen.isAlive() &&
              this.tunnelNetwork.getChamberAt(this.queen.position)?.id === chamber.id;

            // Basic unsuitability - these never change
            const isUnsuitableType = chamber.type === ChamberType.ENTRANCE ||
                                     chamber.type === ChamberType.MIDDEN;

            // Don't drop in the same chamber where we picked up (but timeout after 8s)
            const isPickupChamber = ant.pickupChamberId === chamber.id && carryTime < 8000;

            // Check if this chamber should be avoided (recently found full)
            // Very eager ants ignore this
            const isAvoided = !isVeryEager &&
                              ant.avoidChambersUntil.has(chamber.id) &&
                              this.gameTimeMs < ant.avoidChambersUntil.get(chamber.id)!;

            // Count existing brood in this chamber (eggs + larvae = good company)
            const broodCount = this.countBroodInChamber(chamber.id);
            const hasExistingBrood = broodCount > 0;
            const hasCapacity = broodCount < chamber.eggCapacity;

            // Food repulsion - but very eager ants will tolerate some food
            const foodInChamber = chamber.foodStored ?? 0;
            const tooMuchFood = isVeryEager ? foodInChamber > 100 : foodInChamber > 0;

            // Decision logic based on eagerness
            if (!isUnsuitableType && !queenInChamber && !isPickupChamber && !isAvoided) {
              let shouldDrop = false;

              if (isVeryEager && hasCapacity && !tooMuchFood) {
                // Very eager: drop in any chamber with capacity
                shouldDrop = true;
              } else if (isEager && hasCapacity) {
                // Eager: drop if no food, or 30% chance with food
                if (foodInChamber <= 0) {
                  shouldDrop = true;
                } else if (this.rng.chance(0.3)) {
                  shouldDrop = true;
                }
              } else if (hasCapacity && hasExistingBrood) {
                // Not eager yet: only drop if there's existing brood (clustering)
                if (foodInChamber <= 0) {
                  shouldDrop = true;
                } else if (this.rng.chance(0.1)) {
                  // 10% chance to drop even with food if brood present
                  shouldDrop = true;
                }
              }

              if (shouldDrop) {
                // Drop egg here
                for (const brood of this.brood.values()) {
                  if (brood.carriedByAntId === ant.id) {
                    brood.carriedByAntId = null;
                    brood.chamberId = chamber.id;
                    chamber.eggCount++;
                    const angle = this.rng.next() * Math.PI * 2;
                    const dist = this.rng.next() * chamber.radius * 0.6;
                    brood.position = {
                      x: chamber.position.x + Math.cos(angle) * dist,
                      y: chamber.position.y + Math.sin(angle) * dist,
                    };
                    brood.droppedTime = this.gameTimeMs;
                    break;
                  }
                }
                ant.carryingEgg = false;
                ant.pickupChamberId = null;
                ant.stats.eggsNursed++;
                ant.task = AntTask.NONE;
                ant.taskTimer = 0;  // Reset timer to start rest period
                break;
              } else if (!hasCapacity) {
                // Chamber is full - avoid for a while (shorter if eager)
                const avoidTime = isEager ? 2000 : 5000;
                ant.avoidChambersUntil.set(chamber.id, this.gameTimeMs + avoidTime);
              } else if (foodInChamber > 0 && !isVeryEager) {
                // Has food but we're not desperate - avoid briefly
                ant.avoidChambersUntil.set(chamber.id, this.gameTimeMs + 3000);
              }
            }
          }
        }
        // Not in a suitable chamber - keep following BROOD_SCENT to find one
        if (this.followScent(ant, dt)) {
          // Arrived but couldn't drop egg - wander to find another chamber
          this.wanderAnt(ant, dt);
        }
        break;

      case AntTask.CARRYING_FOOD:
        // Carrying food - react locally to hungry larvae/queen when found
        {
          const chamber = ant.currentChamberId !== null
            ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
            : this.tunnelNetwork.getChamberAt(ant.position);

          if (chamber && ant.carriedFoodAmount > 0) {
            // Check for hungry larvae in this chamber - feed ALL of them!
            const larvaeHere = Array.from(this.brood.values()).filter(
              b => b.stage === BroodStage.LARVA &&
                   b.chamberId === chamber.id &&
                   b.fed < this.broodConfig.LARVA_FOOD_NEEDED
            );
            for (const larva of larvaeHere) {
              if (ant.carriedFoodAmount <= 0) break; // Out of food
              const feedAmount = Math.min(ant.carriedFoodAmount, this.broodConfig.LARVA_FOOD_NEEDED - larva.fed);
              larva.fed += feedAmount;
              ant.carriedFoodAmount -= feedAmount;
              ant.stats.eggsNursed++;
            }
            if (ant.carriedFoodAmount <= 0) {
              ant.carryingFood = false;
              ant.task = AntTask.NONE;
              ant.taskTimer = 0;  // Reset timer to start rest period
              break;
            }

            // Check for hungry queen in this chamber
            const queenHere = this.queen && this.queen.isAlive() &&
              this.tunnelNetwork.getChamberAt(this.queen.position)?.id === chamber.id;
            if (queenHere && this.queenHunger > 20) {
              this.queenHunger = Math.max(0, this.queenHunger - this.colonyConfig.QUEEN_FEED_AMOUNT);
              ant.stats.eggsNursed++;
              ant.carryingFood = false;
              ant.carriedFoodAmount = 0;
              ant.task = AntTask.NONE;
              ant.taskTimer = 0;  // Reset timer to start rest period
              break;
            }
          }
        }
        // Keep following scent to find hungry brood/queen
        if (this.followScent(ant, dt)) {
          // Arrived but no one to feed - wander to find hungry brood
          this.wanderAnt(ant, dt);
        }
        break;

      default:
        // Nurses should never be idle - reset to NONE to re-evaluate priorities
        ant.task = AntTask.NONE;
        break;
    }
  }

  /**
   * Set ant's path to the nursery.
   */
  private setPathToNursery(ant: Ant): void {
    this.navigationSystem.setPathToNursery(ant, this.getNavigationContext());
  }

  /**
   * Update soldier behavior - local sensing and reactive.
   */
  private updateSoldier(ant: Ant, dt: number): void {
    ant.taskTimer += dt;

    // Get current chamber for local sensing
    const currentChamber = ant.currentChamberId !== null
      ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
      : this.tunnelNetwork.getChamberAt(ant.position);

    // Check for alarm scent - immediate response
    const alarmLevel = currentChamber?.scents?.[ScentType.ALARM] ?? 0;
    if (alarmLevel > 0.3 && ant.task !== AntTask.RESPONDING_ALARM) {
      ant.task = AntTask.RESPONDING_ALARM;
      ant.taskTimer = 0;
      this.setScent(ant, ScentType.ALARM);
    }

    switch (ant.task) {
      case AntTask.NONE:
        // At entrance? Consider guarding
        if (currentChamber && currentChamber.type === ChamberType.ENTRANCE) {
          if (this.rng.chance(0.6)) {
            ant.task = AntTask.GUARDING;
            ant.taskTimer = 0;
            // Still move this frame
            this.wanderAnt(ant, dt);
            break;
          }
        }
        // Otherwise patrol - set task and start moving
        ant.task = AntTask.PATROLLING;
        ant.taskTimer = 0;
        this.setScent(ant, ScentType.NEST_ODOR);
        this.followScent(ant, dt);
        break;

      case AntTask.PATROLLING:
        // At entrance? Maybe switch to guarding
        if (currentChamber && currentChamber.type === ChamberType.ENTRANCE) {
          if (this.rng.chance(0.3)) {
            ant.task = AntTask.GUARDING;
            ant.taskTimer = 0;
            break;
          }
        }
        // After 30 seconds, switch to guarding
        if (ant.taskTimer > 30000) {
          ant.task = AntTask.GUARDING;
          ant.taskTimer = 0;
          this.setPathToEntrance(ant);
          break;
        }
        // Keep patrolling
        if (this.followScent(ant, dt)) {
          // Arrived at scent destination - keep wandering
          this.wanderAnt(ant, dt);
        }
        break;

      case AntTask.GUARDING:
        // Not at entrance? Go there
        if (!currentChamber || currentChamber.type !== ChamberType.ENTRANCE) {
          this.setPathToEntrance(ant);
          if (this.followScent(ant, dt)) {
            // Arrived but not at entrance - keep moving
            this.wanderAnt(ant, dt);
          }
          break;
        }
        // Guard for up to 60 seconds
        if (ant.taskTimer > 60000) {
          ant.task = AntTask.PATROLLING;
          ant.taskTimer = 0;
        }
        ant.state = AntState.GUARDING;
        this.wanderAnt(ant, dt);
        break;

      case AntTask.RESPONDING_ALARM:
        if (this.followScent(ant, dt)) {
          ant.stats.threatsRepelled++;
          ant.task = AntTask.NONE;
          ant.taskTimer = 0;
        }
        if (ant.taskTimer > 20000) {
          ant.task = AntTask.NONE;
          ant.taskTimer = 0;
        }
        break;

      default:
        ant.task = AntTask.NONE;
        break;
    }
  }

  /**
   * Update engineer behavior - local sensing for damage detection.
   */
  private updateEngineer(ant: Ant, dt: number): void {
    ant.taskTimer += dt;

    // Get current chamber and tunnel for local sensing
    const currentChamber = ant.currentChamberId !== null
      ? this.tunnelNetwork.getChamberById(ant.currentChamberId)
      : this.tunnelNetwork.getChamberAt(ant.position);

    const currentTunnel = ant.currentTunnelId !== null
      ? this.tunnelNetwork.getTunnelById(ant.currentTunnelId)
      : null;

    // LOCAL SENSING: Check for damage at current location
    const damageHere = this.checkLocalDamage(ant, currentChamber, currentTunnel);

    switch (ant.task) {
      case AntTask.NONE:
        // Found damage? Start repairing!
        if (damageHere) {
          ant.task = AntTask.REPAIRING;
          ant.taskTimer = 0;
          ant.repairTarget = damageHere;
          break;
        }
        // No damage found - patrol to find some (and start moving)
        ant.task = AntTask.PATROLLING;
        ant.taskTimer = 0;
        this.setScent(ant, ScentType.NEST_ODOR);
        this.followScent(ant, dt);
        break;

      case AntTask.PATROLLING:
        // Found damage while patrolling? Start repairing!
        if (damageHere) {
          ant.task = AntTask.REPAIRING;
          ant.taskTimer = 0;
          ant.repairTarget = damageHere;
          break;
        }
        // Keep patrolling
        if (this.followScent(ant, dt)) {
          // Arrived at destination - keep wandering to find damage
          this.wanderAnt(ant, dt);
        }
        // After 20 seconds, switch to NONE to re-evaluate
        if (ant.taskTimer > 20000) {
          ant.task = AntTask.NONE;
          ant.taskTimer = 0;
        }
        break;

      case AntTask.REPAIRING:
        // Work on repair
        {
          const target = ant.repairTarget;
          if (!target) {
            ant.task = AntTask.NONE;
            break;
          }

          if (target.type === 'tunnel') {
            // Tunnel repair takes 15 seconds
            if (ant.taskTimer >= 15000) {
              this.tunnelNetwork.repairTunnel(target.id);
              ant.stats.tunnelsDug++;
              this.addNews(`Tunnel repaired by ${ant.getDisplayName()}`, 'info');
              ant.repairTarget = null;
              ant.task = AntTask.NONE;
              ant.taskTimer = 0;
            }
          } else if (target.type === 'chamber') {
            // Chamber rebuild takes 30 seconds
            if (ant.taskTimer >= 30000) {
              const chamber = this.tunnelNetwork.getChamberById(target.id);
              if (chamber) {
                this.tunnelNetwork.rebuildChamber(target.id);
                ant.stats.tunnelsDug++;
                this.addNews(`${this.getChamberLabel(chamber)} rebuilt by ${ant.getDisplayName()}!`, 'info');
              }
              ant.repairTarget = null;
              ant.task = AntTask.NONE;
              ant.taskTimer = 0;
            }
          }
        }
        break;

      default:
        ant.task = AntTask.NONE;
        break;
    }
  }

  /**
   * Check for damage near an engineer's current position (local sensing).
   */
  private checkLocalDamage(
    ant: Ant,
    currentChamber: Chamber | null | undefined,
    currentTunnel: ReturnType<TunnelNetwork['getTunnelById']> | null
  ): { type: string; id: number } | null {
    // Check if current tunnel is collapsed
    if (currentTunnel && currentTunnel.collapsed) {
      return { type: 'tunnel', id: currentTunnel.id };
    }

    // Check if current chamber is destroyed
    if (currentChamber && currentChamber.destroyed) {
      return { type: 'chamber', id: currentChamber.id };
    }

    // Check adjacent tunnels from current chamber
    if (currentChamber) {
      for (const tunnelId of currentChamber.connectedTunnels) {
        const tunnel = this.tunnelNetwork.getTunnelById(tunnelId);
        if (tunnel && tunnel.collapsed) {
          return { type: 'tunnel', id: tunnel.id };
        }
      }
    }

    return null;
  }

  /**
   * Update queen behavior (minimal movement).
   */
  private updateQueen(ant: Ant, dt: number): void {
    // Queen mostly stays in her chamber
    const queenChamber = this.tunnelNetwork.getQueenChamber();
    if (queenChamber) {
      const dist = Math.sqrt(
        Math.pow(ant.position.x - queenChamber.position.x, 2) +
        Math.pow(ant.position.y - queenChamber.position.y, 2)
      );
      if (dist > queenChamber.radius * 0.5) {
        // Move toward center
        ant.targetPosition = { ...queenChamber.position };
        this.moveTowardTarget(ant, dt);
      } else {
        ant.state = AntState.IDLE;
      }
    }
  }

  // ======== Navigation Methods (delegated to NavigationSystem) ========

  private getNavigationContext(): NavigationContext {
    return {
      tunnelNetwork: this.tunnelNetwork,
      gameTimeMs: this.gameTimeMs,
      rng: this.rng,
    };
  }

  /**
   * Set ant's path to an entrance.
   */
  private setPathToEntrance(ant: Ant): void {
    this.navigationSystem.setPathToEntrance(ant);
  }

  /**
   * Set ant's path to a storage chamber.
   */
  private setPathToStorage(ant: Ant): void {
    this.navigationSystem.setPathToStorage(ant, this.getNavigationContext());
  }

  /**
   * Set ant's path to the queen.
   */
  private setPathToQueen(ant: Ant): void {
    this.navigationSystem.setPathToQueen(ant, this.getNavigationContext());
  }

  /**
   * Set the scent an ant should follow.
   */
  private setScent(ant: Ant, scentType: ScentType): void {
    this.navigationSystem.setScent(ant, scentType, this.getNavigationContext());
  }

  /**
   * Follow the current scent preference. Returns true if at a chamber with high scent.
   */
  private followScent(ant: Ant, dt: number): boolean {
    return this.navigationSystem.followScent(ant, dt, this.getNavigationContext());
  }

  /**
   * Move ant toward its target position. Returns true if reached.
   */
  private moveTowardTarget(ant: Ant, dt: number): boolean {
    return this.navigationSystem.moveTowardTarget(ant, dt);
  }

  /**
   * Make ant wander randomly within the colony.
   */
  private wanderAnt(ant: Ant, dt: number): void {
    this.navigationSystem.wanderAnt(ant, dt, this.getNavigationContext());
  }

  /**
   * Deposit food in a suitable chamber (with capacity and repulsion checks).
   * Returns true if food was deposited, false if ant should keep looking.
   */
  private depositFood(ant: Ant): boolean {
    const currentChamber = this.tunnelNetwork.getChamberAt(ant.position);
    if (!currentChamber) return false;

    // Find a suitable chamber for food
    const targetChamber = this.findChamberForFood(currentChamber, ant.carriedFoodAmount);
    if (targetChamber) {
      // Mix the food color using weighted average
      const oldFood = targetChamber.foodStored;
      const newFood = ant.carriedFoodAmount;
      const totalFood = oldFood + newFood;
      if (totalFood > 0) {
        // Weighted average: (oldHue * oldFood + newHue * newFood) / totalFood
        targetChamber.foodHue = (targetChamber.foodHue * oldFood + ant.carriedFoodHue * newFood) / totalFood;
      }
      targetChamber.foodStored = totalFood;
      this.stats.foodGathered += ant.carriedFoodAmount;
      ant.stats.foodGathered += ant.carriedFoodAmount;
      return true;
    }
    return false;
  }

  /**
   * Handle queen death and start replacement process.
   */
  private handleQueenDeath(): void {
    if (!this.queen) return;

    this.killAnt(this.queen, 'starvation');
    this.colonyState = 'queenless';
    this.addNews('The colony is queenless! A replacement must emerge.', 'alert');

    // Select a nurse for replacement
    const nurses = Array.from(this.ants.values()).filter(
      a => a.role === AntRole.NURSE && a.isAlive()
    );
    if (nurses.length > 0) {
      this.replacementCandidate = this.rng.pick(nurses)!;
      this.replacementCandidate.task = AntTask.TRANSITIONING;
      this.colonyState = 'replacing_queen';
      this.replacementTimer = 0;
      this.addNews(`${this.replacementCandidate.getDisplayName()} begins transformation...`, 'info');
    }
  }

  /**
   * Update queen replacement process.
   */
  private updateQueenReplacement(dt: number): void {
    if (this.colonyState !== 'replacing_queen' || !this.replacementCandidate) return;

    this.replacementTimer += dt;

    // Transition takes 30 seconds
    if (this.replacementTimer >= 30000) {
      // Create new queen from the candidate
      const newQueen = this.replacementCandidate;

      // Transform the ant (keep same object but update role-like properties)
      // Since role is readonly, we need to create a new queen and remove the old ant
      const queenChamber = this.tunnelNetwork.getQueenChamber();
      const position = queenChamber ? queenChamber.position : newQueen.position;

      // Remove old ant
      this.ants.delete(newQueen.id);

      // Create actual queen
      this.queen = this.createAnt(AntRole.QUEEN, position);
      this.queen.name = AntNameGenerator.generateQueenName(8); // New generation
      this.queen.health = 150; // Reduced fertility (lower max health)

      this.colonyState = 'normal';
      this.queenHunger = 0;
      this.stats.queenStatus = 'alive';
      this.replacementCandidate = null;

      this.addNews(`Long live ${this.queen.name}!`, 'info');
    }
  }

  /**
   * Try to send an ant to the surface through a portal.
   * Returns true if successful, false otherwise.
   */
  private tryExitToSurface(ant: Ant): boolean {
    // Find nearest entrance portal
    const entrances = this.tunnelNetwork.getEntrances();
    if (entrances.length === 0) {
      ant.task = AntTask.NONE; // Reset task if no entrances
      return false;
    }

    let nearestEntrance: Chamber | null = null;
    let nearestDist = Infinity;

    for (const entrance of entrances) {
      if (entrance.destroyed) continue;
      const dx = entrance.position.x - ant.position.x;
      const dy = entrance.position.y - ant.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearestEntrance = entrance;
      }
    }

    if (nearestEntrance && nearestDist < 50) {
      // Find corresponding portal
      const portal = this.portals.find(p =>
        Math.abs(p.position.x - nearestEntrance!.position.x) < 10 &&
        Math.abs(p.position.y - nearestEntrance!.position.y) < 10
      );

      if (portal) {
        // Create the entity and emit for transfer
        const entity = this.sendAntToSurface(ant.id, portal.id);
        if (entity) {
          // Emit transfer request for Game to handle (synchronous — confirmAntOnSurface is called inline on success)
          this.events.emit('ANT_TRANSFER_REQUEST', {
            entity,
            targetLayer: portal.targetLayerId,
            targetPortal: portal.targetPortalId,
          }, this.id);
          // Check if the transfer actually succeeded (ant was confirmed on surface)
          return this.antsOnSurface.has(ant.id);
        }
      }
    }

    // Failed to exit - reset task so ant doesn't get stuck
    ant.task = AntTask.NONE;
    return false;
  }
  private getChamberLabel(chamber: Chamber): string {
    switch (chamber.type) {
      case ChamberType.QUEEN_CHAMBER:
        return 'Queen\'s Chamber';
      case ChamberType.NURSERY:
        return 'Nursery';
      case ChamberType.FOOD_STORAGE:
        return 'Food Storage';
      case ChamberType.ENTRANCE:
        return 'Entrance';
      case ChamberType.MIDDEN:
        return 'Midden';
      default:
        return '';
    }
  }

  // Public getters for stats
  getStats(): ColonyStats {
    return { ...this.stats };
  }

  /**
   * A canonical, order-stable view of everything this colony has decided.
   *
   * Feed to `hashState()` to compare two runs. Deliberately excludes anything
   * that is not a simulation decision:
   *
   * - **View state** -- pan, zoom, hover and selection are the client's, and
   *   since ADR-007 they are not even in this file. The mayor looking somewhere
   *   else must not change the ledger.
   * - **Debris particles** -- cosmetic, drawn from a forked stream, and absent
   *   entirely from a headless run.
   * - **News timestamps** -- sim-time since ADR-005, but still unhashed: only
   *   the message text is compared. Including the stamp would move every
   *   recorded colony hash (`docs/determinism.md`, known gaps).
   *
   * Not the same thing as `snapshot()`, which is total. This answers *did two
   * runs agree*; that one answers *can this colony be continued*.
   */
  canonicalState(): Record<string, Canonical> {
    // Ants: sorted by id so the hash cannot depend on Map insertion order,
    // which differs between a fresh run and a restored one.
    const ants = Array.from(this.ants.values())
      .sort((a, b) => a.id - b.id)
      .map((a) => ({
        id: a.id,
        name: a.name,
        role: a.role,
        state: a.state,
        task: a.task,
        x: a.position.x,
        y: a.position.y,
        health: a.health,
        hunger: a.hunger,
        fatigue: a.fatigue,
        age: a.age,
        injured: a.injured,
        scarred: a.scarred,
        carryingFood: a.carryingFood,
        carryingEgg: a.carryingEgg,
        chamberId: a.currentChamberId,
        tunnelId: a.currentTunnelId,
      }));

    const brood = Array.from(this.brood.values())
      .sort((a, b) => a.id - b.id)
      .map((b) => ({
        id: b.id,
        stage: b.stage,
        chamberId: b.chamberId,
        age: b.age,
        fed: b.fed,
        carriedBy: b.carriedByAntId,
      }));

    const chambers = this.tunnelNetwork
      .getAllChambers()
      .slice()
      .sort((a, b) => a.id - b.id)
      .map((c) => ({
        id: c.id,
        destroyed: c.destroyed,
        foodStored: c.foodStored,
        eggCount: c.eggCount,
        scents: c.scents as unknown as Canonical,
        stimuli: c.stimuli as unknown as Canonical,
      }));

    const tunnels = this.tunnelNetwork
      .getAllTunnels()
      .slice()
      .sort((a, b) => a.id - b.id)
      .map((t) => ({ id: t.id, collapsed: t.collapsed }));

    return {
      colonyIndex: this.colonyIndex,
      tick: this.tickCount,
      gameTimeMs: this.gameTimeMs,
      stats: { ...this.stats },
      colonyState: this.colonyState,
      queenHunger: this.queenHunger,
      alertState: this.alertState,
      nextAntId: this.nextAntId,
      antsOnSurface: Array.from(this.antsOnSurface).sort((a, b) => a - b),
      ants,
      brood,
      chambers,
      tunnels,
      // The generator's position in its stream is part of the state: two
      // colonies that agree on every ant but disagree here will diverge on
      // the very next tick.
      rng: this.rng.save() as unknown as Canonical,
      news: this.news.map((n) => `${n.type}:${n.message}`),
    };
  }

  /** Convenience: hash of `canonicalState()`. */
  stateHash(): string {
    return hashState(this.canonicalState());
  }

  getPopulation(): number {
    return this.stats.currentPopulation;
  }

  getLastSurvivor(): string | null {
    // Find the last living ant
    for (const ant of this.ants.values()) {
      if (ant.isAlive()) {
        return ant.getDisplayName();
      }
    }
    return this.kira?.getDisplayName() || null;
  }

  getAntsOnSurfaceCount(): number {
    return this.antsOnSurface.size;
  }

  getTunnelNetwork(): TunnelNetwork {
    return this.tunnelNetwork;
  }

  getColonyIndex(): number {
    return this.colonyIndex;
  }

  /** Get territory percentage (0-1). */
  getTerritoryPercentage(): number {
    return this.tunnelNetwork.getTerritoryPercentage();
  }

  /** Get total food stored in the colony. */
  getTotalFood(): number {
    return this.tunnelNetwork.getTotalFood();
  }

  /** Get foraging urgency (0-1). */
  getForagingUrgencyPublic(): number {
    return this.getForagingUrgency();
  }

  /** Count foragers currently on the surface. */
  getForagersOutCount(): number {
    let count = 0;
    for (const antId of this.antsOnSurface) {
      const ant = this.ants.get(antId);
      if (ant && ant.isAlive() && ant.role === AntRole.FORAGER) {
        count++;
      }
    }
    return count;
  }

  /** Get brood counts by stage. */
  getBroodCounts(): { eggs: number; larvae: number; pupae: number } {
    let eggs = 0, larvae = 0, pupae = 0;
    for (const b of this.brood.values()) {
      if (b.stage === BroodStage.EGG) eggs++;
      else if (b.stage === BroodStage.LARVA) larvae++;
      else if (b.stage === BroodStage.PUPA) pupae++;
    }
    return { eggs, larvae, pupae };
  }

  /** Get queen hunger percentage (0-100), or -1 if no queen. */
  getQueenHungerPercent(): number {
    if (!this.queen || !this.queen.isAlive()) return -1;
    return this.queenHunger;
  }

  /** Get recent news items. */
  getRecentNews(count: number): Array<{ message: string; type: string }> {
    return this.news.slice(-count).reverse().map(item => ({
      message: item.message,
      type: item.type,
    }));
  }

  /**
   * Set the surface portal position (grid coordinates).
   * Called by ParkingtonLayer after portal setup.
   */
  setSurfacePortalPosition(gridX: number, gridY: number): void {
    this.surfacePortalX = gridX;
    this.surfacePortalY = gridY;
  }

  /**
   * Everything this colony is, in a form it can be rebuilt from.
   *
   * The counterpart of `SurfaceSim.snapshot()`, and since ADR-006 the only
   * serialisation a colony has: the client's save carries one of these per colony
   * rather than a lossy record of its own. Nothing here may be left out.
   *
   * What it deliberately does not carry is the *layout*. Chambers and tunnels are
   * generated from the colony index, so a snapshot is restored into a colony that
   * has already dug itself, and `TunnelNetwork.load()` overlays what the season
   * changed. The alternative — recording eleven chambers and their geometry every
   * time — would be recording arithmetic.
   */
  snapshot(): ColonySnapshot {
    // In `Map` order throughout, which is the order `update()` walks them in and
    // therefore the order they draw from the colony's stream. A snapshot preserves
    // iteration order rather than canonicalising it — see `core/snapshot.ts`, and
    // see the surface ants for what sorting costs.
    const ants = Array.from(this.ants.values()).map((a) => a.save());

    const brood = Array.from(this.brood.values())
      .map((b) => ({
        id: b.id,
        stage: b.stage,
        position: { ...b.position },
        chamberId: b.chamberId,
        age: b.age,
        fed: b.fed,
        carriedByAntId: b.carriedByAntId,
        droppedTime: b.droppedTime,
      }));

    return {
      version: SNAPSHOT_VERSION,
      colonyIndex: this.colonyIndex,
      tick: this.tickCount,
      gameTimeMs: this.gameTimeMs,
      rng: this.rng.save(),
      nextAntId: this.nextAntId,
      nextBroodId: this.nextBroodId,
      ants,
      brood,
      stats: { ...this.stats },
      news: this.news.map((n) => ({ ...n })),
      namedByPlayer: Array.from(this.namedByPlayer),
      antsOnSurface: Array.from(this.antsOnSurface),
      specialAnts: {
        queenId: this.queen?.id ?? null,
        kiraId: this.kira?.id ?? null,
        soldierVexId: this.soldierVex?.id ?? null,
        oldMapId: this.oldMap?.id ?? null,
        philosopherAntId: this.philosopherAnt?.id ?? null,
        replacementCandidateId: this.replacementCandidate?.id ?? null,
      },
      queenHunger: this.queenHunger,
      colonyState: this.colonyState,
      replacementTimer: this.replacementTimer,
      alertState: this.alertState,
      alertTimer: this.alertTimer,
      eggLayTimer: this.eggLayTimer,
      stimuliTimer: this.stimuliTimer,
      pheromoneDecayTimer: this.pheromoneDecayTimer,
      metabolismTimer: this.metabolismSystem.getMetabolismTimer(),
      roleTransitionLastCheck: this.roleTransitionSystem.save(),
      pendingDamage: this.damageSystem.save(),
      surfacePortal: { x: this.surfacePortalX, y: this.surfacePortalY },
      tunnels: this.tunnelNetwork.save(),
    };
  }

  /**
   * Become the colony this snapshot describes.
   *
   * Two things this does that `restoreFromFile()` below does not, and both are the
   * difference between resuming a season and loading a colony off a disk:
   *
   * **It does not judge the ants who are out.** A colony read from a file alone
   * has to decide what to do with the foragers it says were on the surface, since
   * this session's surface has never seen them — and it kills them, which is the
   * only honest answer available to it. A district always brings its surface, so
   * nobody dies of having been restored.
   *
   * **It files no headline.** *"Colony state restored from file"* is true of a
   * file and false of a season: from the colony's point of view the world was
   * never interrupted, and a news item saying otherwise would be a state change
   * caused by the act of snapshotting. Rule 5 asks that restoring be
   * indistinguishable from never having stopped, and a colony that can tell you it
   * was restored is not that.
   */
  restore(state: ColonySnapshot): void {
    requireSnapshotVersion(state.version, 'colony');
    if (state.colonyIndex !== this.colonyIndex) {
      throw new RangeError(
        `colony snapshot is for colony ${state.colonyIndex}, this is colony ` +
        `${this.colonyIndex}. Their tunnel layouts differ`
      );
    }

    this.tickCount = state.tick;
    this.gameTimeMs = state.gameTimeMs;
    this.rng.load(state.rng);
    this.nextAntId = state.nextAntId;
    this.nextBroodId = state.nextBroodId;

    this.ants.clear();
    for (const saved of state.ants) {
      this.ants.set(saved.id, Ant.fromSnapshot(saved, this.roleAttributes));
    }

    this.brood.clear();
    for (const saved of state.brood) {
      this.brood.set(saved.id, {
        id: saved.id,
        stage: saved.stage,
        position: { ...saved.position },
        chamberId: saved.chamberId,
        age: saved.age,
        fed: saved.fed,
        carriedByAntId: saved.carriedByAntId,
        droppedTime: saved.droppedTime,
      });
    }

    this.stats = { ...state.stats };
    this.news = state.news.map((n) => ({ ...n }));

    this.namedByPlayer.clear();
    for (const id of state.namedByPlayer) this.namedByPlayer.add(id);
    this.antsOnSurface.clear();
    for (const id of state.antsOnSurface) this.antsOnSurface.add(id);

    const byId = (id: number | null) => (id === null ? null : this.ants.get(id) ?? null);
    this.queen = byId(state.specialAnts.queenId);
    this.kira = byId(state.specialAnts.kiraId);
    this.soldierVex = byId(state.specialAnts.soldierVexId);
    this.oldMap = byId(state.specialAnts.oldMapId);
    this.philosopherAnt = byId(state.specialAnts.philosopherAntId);
    this.replacementCandidate = byId(state.specialAnts.replacementCandidateId);

    this.queenHunger = state.queenHunger;
    this.colonyState = state.colonyState;
    this.replacementTimer = state.replacementTimer;
    this.alertState = state.alertState;
    this.alertTimer = state.alertTimer;
    this.eggLayTimer = state.eggLayTimer;
    this.stimuliTimer = state.stimuliTimer;
    this.pheromoneDecayTimer = state.pheromoneDecayTimer;
    this.metabolismSystem.setMetabolismTimer(state.metabolismTimer);
    this.roleTransitionSystem.load(state.roleTransitionLastCheck);
    this.damageSystem.load(state.pendingDamage);
    this.surfacePortalX = state.surfacePortal.x;
    this.surfacePortalY = state.surfacePortal.y;

    this.tunnelNetwork.load(state.tunnels);
  }

  /**
   * Restore a colony from a file, on its own, with no surface underneath it.
   *
   * The Formicarium menu's per-colony import (`docs/ui-controls.md`). A district
   * save restores through `restore()` and needs none of this, because it always
   * brings a surface with it; a single colony read from disk does not, and the
   * foragers the file says were above ground are ants this session's surface has
   * never heard of. Nothing would ever bring them home.
   *
   * So they die, and the colony counts them — which is what the old lossy save
   * format did too, and the only honest answer available: a colony whose
   * population includes ants it can never lose has a ledger that is wrong, and
   * the ledger is the one thing here that matters.
   *
   * It files a headline too, for the same reason `restore()` refuses to: this
   * *is* an interruption. The world did stop, and somebody loaded a file.
   */
  restoreFromFile(state: ColonySnapshot): void {
    this.restore(state);

    for (const id of Array.from(this.antsOnSurface)) {
      const ant = this.ants.get(id);
      if (ant && ant.isAlive()) {
        ant.state = AntState.DEAD;
        ant.health = 0;
        this.stats.deaths++;
        this.stats.currentPopulation--;
      }
    }
    this.antsOnSurface.clear();

    for (const ant of this.ants.values()) {
      if (ant.isAlive() && ant.task === AntTask.ON_SURFACE) {
        ant.task = AntTask.NONE;
      }
    }

    this.addNews('Colony state restored from file.', 'info');
  }

  /**
   * Restart the colony to initial state.
   */
  restart(): void {
    // Notify other layers to clear any ants from this colony
    this.events.emit(GameEventTypes.COLONY_RESTARTED, {
      colonyIndex: this.colonyIndex,
    }, this.id);

    // Clear all state
    this.ants.clear();
    this.brood.clear();
    this.namedByPlayer.clear();
    this.antsOnSurface.clear();

    this.news = [];

    // Reset counters
    this.nextAntId = 0;
    this.nextBroodId = 0;
    this.tickCount = 0;

    // Reset queen state
    this.queenHunger = 0;
    this.colonyState = 'normal';
    this.replacementTimer = 0;
    this.replacementCandidate = null;

    // Reset timers
    this.alertState = false;
    this.alertTimer = 0;
    this.eggLayTimer = 0;
    this.pheromoneDecayTimer = 0;

    // Reset systems
    this.metabolismSystem.reset();
    this.damageSystem.reset();

    // Reset stats
    this.stats = {
      initialPopulation: 847,
      currentPopulation: 0,
      peakPopulation: 0,
      deaths: 0,
      births: 0,
      foodGathered: 0,
      queenStatus: 'alive',
      colonyAge: 0,
    };

    // Reset special ants
    this.queen = null;
    this.kira = null;
    this.soldierVex = null;
    this.oldMap = null;
    this.philosopherAnt = null;

    // Regenerate tunnel network (restore to original state)
    this.tunnelNetwork.reset();
    this.tunnelNetwork.generateNetwork();

    // Repopulate the colony
    this.populateColony();

    this.addNews('Colony has been restarted.', 'info');
    const queenName = (this.queen as Ant | null)?.name ?? 'Unknown';
    this.addNews(`Queen ${queenName} presides over ${this.stats.currentPopulation} subjects.`, 'info');
  }
  // ─── Config forwarding for settings panel ───

  updateMetabolismConfig(partial: Partial<MetabolismConfig>): void {
    this.metabolismSystem.updateConfig(partial);
  }

  updateBroodConfig(partial: Partial<BroodSystemConfig>): void {
    this.broodConfig = { ...this.broodConfig, ...partial };
  }

  updateStimulusConfig(partial: Partial<StimulusConfig>): void {
    this.stimConfig = { ...this.stimConfig, ...partial };
  }

  updateRoleTransitionConfig(partial: Partial<RoleTransitionConfig>): void {
    this.roleTransitionSystem.updateConfig(partial);
  }

  updateColonyConfig(partial: Record<string, number>): void {
    /* eslint-disable-next-line no-restricted-syntax -- Rule 4 does not bite: each
       key sets a different config field and sets it once, so the config that comes
       out is the same whatever order the partial is walked in. World parameters,
       not simulation state — a snapshot does not contain them (ADR-005). */
    for (const [key, value] of Object.entries(partial)) {
      if (key in this.colonyConfig) {
        (this.colonyConfig as Record<string, number>)[key] = value;
      }
    }
  }

  updateDamageConfig(partial: Partial<DamageConfig>): void {
    this.damageSystem.updateConfig(partial);
  }

  setRoleAttributes(attrs: Record<AntRole, AntRoleAttributes>): void {
    this.roleAttributes = attrs;
  }

  getRoleAttributes(): Record<AntRole, AntRoleAttributes> {
    return this.roleAttributes;
  }
}
/**
 * Everything a colony is. See `FormicariumLayer.snapshot()`.
 *
 * State, not configuration: nothing here says how fast an egg hatches or how
 * hard a slab hits. Those are world parameters a season is settled *under*.
 */
export interface ColonySnapshot {
  version: number;
  colonyIndex: number;
  tick: number;
  gameTimeMs: number;
  rng: RngState;
  nextAntId: number;
  nextBroodId: number;
  ants: AntSnapshot[];
  brood: Brood[];
  stats: ColonyStats;
  news: ColonyNewsItem[];
  namedByPlayer: number[];
  antsOnSurface: number[];
  specialAnts: SpecialAntIds;
  queenHunger: number;
  colonyState: 'normal' | 'queenless' | 'replacing_queen';
  replacementTimer: number;
  alertState: boolean;
  alertTimer: number;
  eggLayTimer: number;
  stimuliTimer: number;
  pheromoneDecayTimer: number;
  metabolismTimer: number;
  roleTransitionLastCheck: number;
  pendingDamage: PendingDamage;
  surfacePortal: { x: number; y: number };
  tunnels: TunnelNetworkSnapshot;
}

/** The ants the colony holds a reference to, by id. */
export interface SpecialAntIds {
  queenId: number | null;
  kiraId: number | null;
  soldierVexId: number | null;
  oldMapId: number | null;
  philosopherAntId: number | null;
  replacementCandidateId: number | null;
}
